import React, { useState, useEffect, useCallback } from 'react';
import { Key, Check, AlertCircle, Loader2, Save, RefreshCw, Settings, FileText, Database, Zap, TestTube, Activity, ShieldAlert, CreditCard, BarChart3, Gift, Trash2, AlertTriangle, Users, Clock, Power, Video, Receipt, DollarSign, ShieldOff, Unlock, Play, TrendingUp, Wand2, Star, LayoutDashboard, UserPlus, Percent, CheckCircle2, CloudRain } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminConfig() {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Config state
  const [config, setConfig] = useState(null);
  const [saJsonPreview, setSaJsonPreview] = useState(null);

  // Form states for each section
  const [projectNumber, setProjectNumber] = useState('');
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [saChanged, setSaChanged] = useState(false);

  const [bucketName, setBucketName] = useState('');
  const [bucketPath, setBucketPath] = useState('outputs/');
  const [videoExpiryDays, setVideoExpiryDays] = useState(7);

  const [promptText, setPromptText] = useState('');
  const [defaultAnimate, setDefaultAnimate] = useState('');
  const [veoPromptText, setVeoPromptText] = useState('');
  const [veoDefaultAnimate, setVeoDefaultAnimate] = useState('');
  const [bypassKeywords, setBypassKeywords] = useState('');  // newline-separated for the textarea

  // Image Transformation (Gemini 3 Pro Image) prompt management.
  // Prefixes are edited individually per key; master template uses
  // {season}/{daytime}/{weather}/{resolution}/{user_input}.
  const [transformSeasonPrefixes, setTransformSeasonPrefixes] = useState({
    summer: '', winter: '', autumn: '', spring: ''
  });
  const [transformDaytimePrefixes, setTransformDaytimePrefixes] = useState({
    morning: '', afternoon: '', evening: '', late_night: ''
  });
  const [transformWeatherPrefixes, setTransformWeatherPrefixes] = useState({
    rainfall: '', snowfall: '', overcast: '', clear_sky: ''
  });
  const [transformResolutionPrefixes, setTransformResolutionPrefixes] = useState({
    '1080p': '', '4k': ''
  });
  const [transformMasterTemplate, setTransformMasterTemplate] = useState('');
  const [transformDescriptionPlaceholder, setTransformDescriptionPlaceholder] = useState('');

  // Rain Canvas SAM masking prompts (comma-separated, tried in order)
  const RAIN_DEFAULT_INTERIOR = 'view through the glass, window, sky, outdoors';
  const RAIN_DEFAULT_OUTDOORS = 'outdoors, sky, background, trees';
  const [rainInteriorPrompts, setRainInteriorPrompts] = useState(RAIN_DEFAULT_INTERIOR);
  const [rainOutdoorsPrompts, setRainOutdoorsPrompts] = useState(RAIN_DEFAULT_OUTDOORS);

  const [stripeKey, setStripeKey] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [stripeKeyChanged, setStripeKeyChanged] = useState(false);
  const [stripeWebhookChanged, setStripeWebhookChanged] = useState(false);

  // Platform health state
  const [platformUsage, setPlatformUsage] = useState(null);
  const [dailyVideoLimit, setDailyVideoLimit] = useState(500);
  const [dailyImageLimit, setDailyImageLimit] = useState(1000);
  const [savingLimits, setSavingLimits] = useState(false);

  // Gift credits state
  const [gifts, setGifts] = useState([]);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftCredits, setGiftCredits] = useState(3000);
  const [giftExpiry, setGiftExpiry] = useState(10);
  const [giftLoading, setGiftLoading] = useState(false);
  const [deletingGift, setDeletingGift] = useState(null);
  const [checkEmail, setCheckEmail] = useState('');
  const [checkedUser, setCheckedUser] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  // Error logs state
  const [errorLogs, setErrorLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState('ALL');

  // Users tab state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Restore deleted email state
  const [restoreEmail, setRestoreEmail] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  // Force-claim gifts state
  const [forceClaimEmail, setForceClaimEmail] = useState('');
  const [forceClaimLoading, setForceClaimLoading] = useState(false);
  const [forceClaimResult, setForceClaimResult] = useState(null);

  // Adjust credits (refund) state
  const [adjustEmail, setAdjustEmail] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustBucket, setAdjustBucket] = useState('auto');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustCancelSub, setAdjustCancelSub] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustResult, setAdjustResult] = useState(null);

  // Generation requests tab state
  const [genRequests, setGenRequests] = useState([]);
  const [requestsPage, setRequestsPage] = useState(1);
  const REQUESTS_PER_PAGE = 50;
  const [genRequestsLoading, setGenRequestsLoading] = useState(false);

  // Maintenance mode state — controls site-wide kill switch from Platform Health tab
  const [maintEnabled, setMaintEnabled] = useState(false);
  const [maintMessage, setMaintMessage] = useState('');
  const [maintLoading, setMaintLoading] = useState(false);
  const [maintSaving, setMaintSaving] = useState(false);

  // Replicate (video API) config state — used by the "Replicate" admin tab
  const [repApiToken, setRepApiToken] = useState('');
  const [repSigningKey, setRepSigningKey] = useState('');
  const [repWebhookBaseUrl, setRepWebhookBaseUrl] = useState('');
  const [repWebhookUrl, setRepWebhookUrl] = useState('');
  const [repTokenSet, setRepTokenSet] = useState(false);
  const [repSigningKeySet, setRepSigningKeySet] = useState(false);
  const [repApiTokenChanged, setRepApiTokenChanged] = useState(false);
  const [repSigningKeyChanged, setRepSigningKeyChanged] = useState(false);
  const [repLoading, setRepLoading] = useState(false);
  const [repSaving, setRepSaving] = useState(false);

  // Transactions tab state — top-ups + subscriptions browser
  const [txs, setTxs] = useState([]);
  const [txSummary, setTxSummary] = useState(null);
  const [txTotalCount, setTxTotalCount] = useState(0);
  const [txLoading, setTxLoading] = useState(false);
  const [txFilterType, setTxFilterType] = useState('all');     // 'all' | 'topup' | 'subscription'
  const [txFilterStatus, setTxFilterStatus] = useState('all'); // 'all' | 'completed' | 'initiated' | 'failed'
  const [txEmailSearch, setTxEmailSearch] = useState('');
  const [txPage, setTxPage] = useState(0);
  const TX_PAGE_SIZE = 50;

  // Paying Users tab — credit liability + subscriber profitability tracker
  const [payRows, setPayRows] = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payScope, setPayScope] = useState('all');                  // 'all' | 'subscribers' | 'topup'
  const [payEmailSearch, setPayEmailSearch] = useState('');
  const [paySortBy, setPaySortBy] = useState('renewal_at');         // 'renewal_at' | 'sub_credits' | 'topup_credits' | 'lifetime_spent'
  const [paySortDir, setPaySortDir] = useState('asc');
  const [payPage, setPayPage] = useState(0);                        // client-side pagination for 100+ users
  const PAY_PAGE_SIZE = 25;

  // Overview dashboard (landing tab) — executive KPI snapshot
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  // Stripe diagnostic + reconcile (paying-users tab)
  const [stripeDiagEmail, setStripeDiagEmail] = useState('');
  const [stripeDiagLoading, setStripeDiagLoading] = useState(false);
  const [stripeDiagResult, setStripeDiagResult] = useState(null);
  const [stripeReconcileLoading, setStripeReconcileLoading] = useState(false);

  // Daily generations chart (Platform Health tab) — switchable 7d / 30d window
  const [dailyGenRange, setDailyGenRange] = useState(30);
  const [dailyGenBuckets, setDailyGenBuckets] = useState([]);
  const [dailyGenLoading, setDailyGenLoading] = useState(false);

  // Video pricing tab — per-second rates for Steady Vibe (Kling) and Superior Motion (Seedance)
  const [pricingKlingSec1080, setPricingKlingSec1080] = useState(25);
  const [pricingKlingSec720, setPricingKlingSec720] = useState(20);
  const [pricingVeoSec1080, setPricingVeoSec1080] = useState(75);
  const [pricingVeoSec720, setPricingVeoSec720] = useState(45);
  const [pricingImg1080, setPricingImg1080] = useState(20);
  const [pricingImg1440, setPricingImg1440] = useState(40);
  const [pricingImg4k, setPricingImg4k] = useState(60);
  // Service-level pricing (overlays, image transformation)
  const [pricingOverlay, setPricingOverlay] = useState(40);
  const [pricingTransform1080p, setPricingTransform1080p] = useState(45);
  const [pricingTransform4k, setPricingTransform4k] = useState(70);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingSaving, setPricingSaving] = useState(false);

  // Access control tab — unblock IPs (24h signup ledger) + restore deleted emails
  const [accIpRows, setAccIpRows] = useState([]);
  const [accDeletedRows, setAccDeletedRows] = useState([]);
  const [accIpTotal, setAccIpTotal] = useState(0);
  const [accDeletedTotal, setAccDeletedTotal] = useState(0);
  const [accLoading, setAccLoading] = useState(false);
  const [accIpSearch, setAccIpSearch] = useState('');
  const [accEmailSearch, setAccEmailSearch] = useState('');
  const [accManualIp, setAccManualIp] = useState('');
  const [accManualEmail, setAccManualEmail] = useState('');
  const [accIpPage, setAccIpPage] = useState(1);
  const ACC_IP_PER_PAGE = 50;
  // Manual admin block lists (permanent). Farmers who use 3+ accounts daily
  // and cycle IPs/fingerprints slip past the auto-heuristics — the admin
  // needs a direct "block this email / block this IP" hammer.
  const [accBlockedEmails, setAccBlockedEmails] = useState([]);
  const [accBlockedIps, setAccBlockedIps] = useState([]);
  const [manualBlockEmail, setManualBlockEmail] = useState('');
  const [manualBlockEmailReason, setManualBlockEmailReason] = useState('');
  const [manualBlockIp, setManualBlockIp] = useState('');
  const [manualBlockIpReason, setManualBlockIpReason] = useState('');

  // Blocked-device (fingerprint) admin panel — see /api/admin/fingerprints/*.
  const [fpSuspicious, setFpSuspicious] = useState([]);
  const [fpBlocked, setFpBlocked] = useState([]);
  const [fpLoading, setFpLoading] = useState(false);
  const [fpBusy, setFpBusy] = useState(false);
  const [fpManual, setFpManual] = useState('');

  // Tutorials tab — list + edit existing, add new (uses same form for both)
  const [tutList, setTutList] = useState([]);
  const [tutLoading, setTutLoading] = useState(false);
  const [tutSaving, setTutSaving] = useState(false);
  const [tutForm, setTutForm] = useState({
    id: null, title: '', url: '', description: '', display_order: 0, published: false,
  });

  // Tutorials page hero (title + subtitle) — public copy on /tutorials
  const [tutHeader, setTutHeader] = useState({ title: '', subtitle: '' });
  const [tutHeaderLoading, setTutHeaderLoading] = useState(false);
  const [tutHeaderSaving, setTutHeaderSaving] = useState(false);

  const fetchTutorialsHeader = useCallback(async () => {
    setTutHeaderLoading(true);
    try {
      const res = await axios.get(`${API}/tutorials-header`);
      setTutHeader({ title: res.data.title || '', subtitle: res.data.subtitle || '' });
    } catch (err) {
      toast.error('Failed to load tutorials header');
    } finally {
      setTutHeaderLoading(false);
    }
  }, []);

  const saveTutorialsHeader = async () => {
    const title = (tutHeader.title || '').trim();
    const subtitle = (tutHeader.subtitle || '').trim();
    if (!title) { toast.error('Title is required'); return; }
    if (title.length > 200) { toast.error('Title max 200 chars'); return; }
    if (subtitle.length > 1000) { toast.error('Subtitle max 1000 chars'); return; }
    setTutHeaderSaving(true);
    try {
      const res = await axios.post(`${API}/admin/tutorials-header`, { password, title, subtitle });
      setTutHeader({ title: res.data.title, subtitle: res.data.subtitle });
      toast.success('Tutorials header updated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save header');
    } finally {
      setTutHeaderSaving(false);
    }
  };

  const fetchTutorials = useCallback(async () => {
    setTutLoading(true);
    try {
      const res = await axios.post(`${API}/admin/tutorials/list`, { password });
      setTutList(res.data.tutorials || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load tutorials');
    } finally {
      setTutLoading(false);
    }
  }, [password]);

  const resetTutForm = () => setTutForm({
    id: null, title: '', url: '', description: '', display_order: 0, published: false,
  });

  const editTutorial = (t) => setTutForm({
    id: t.id,
    title: t.title || '',
    url: t.url || '',
    description: t.description || '',
    display_order: t.display_order ?? 0,
    published: !!t.published,
  });

  const saveTutorial = async () => {
    if (!tutForm.title.trim()) { toast.error('Title required'); return; }
    if (!tutForm.url.trim())   { toast.error('YouTube URL required'); return; }
    setTutSaving(true);
    try {
      await axios.post(`${API}/admin/tutorials/save`, {
        password,
        id: tutForm.id,
        title: tutForm.title,
        url: tutForm.url,
        description: tutForm.description,
        display_order: parseInt(tutForm.display_order, 10) || 0,
        published: !!tutForm.published,
      });
      toast.success(tutForm.id ? 'Tutorial updated.' : 'Tutorial created.');
      resetTutForm();
      await fetchTutorials();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save tutorial');
    } finally {
      setTutSaving(false);
    }
  };

  const deleteTutorial = async (id, title) => {
    if (!window.confirm(`Delete tutorial "${title}"? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/admin/tutorials/delete`, { password, id });
      toast.success('Tutorial deleted.');
      if (tutForm.id === id) resetTutForm();
      await fetchTutorials();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchAccessBlocks = useCallback(async () => {
    setAccLoading(true);
    try {
      const res = await axios.post(`${API}/admin/access/recent-blocks?limit=200`, { password });
      setAccIpRows(res.data.signup_ips || []);
      setAccDeletedRows(res.data.deleted_emails || []);
      setAccIpTotal(res.data.signup_ips_total || 0);
      setAccDeletedTotal(res.data.deleted_emails_total || 0);
      setAccBlockedEmails(res.data.blocked_emails || []);
      setAccBlockedIps(res.data.blocked_ips || []);
      setAccIpPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load access blocks');
    } finally {
      setAccLoading(false);
    }
  }, [password]);

  const clearIpBlock = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`Clear the 24h signup limit for IP ${ip}? Next signup attempt from this IP will be allowed.`)) return;
    try {
      const res = await axios.post(`${API}/admin/access/clear-ip`, { password, ip });
      toast.success(`Cleared ${res.data.deleted_count} entr${res.data.deleted_count === 1 ? 'y' : 'ies'} for ${ip}.`);
      setAccManualIp('');
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clear IP');
    }
  };

  const restoreDeletedEmail = async (email) => {
    if (!email) return;
    if (!window.confirm(`Restore ${email}? They will be able to sign in with Google again and receive a fresh 40-credit signup bonus.`)) return;
    try {
      await axios.post(`${API}/admin/restore-email`, { password, email });
      toast.success(`${email} restored — they can sign up again.`);
      setAccManualEmail('');
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore email');
    }
  };

  // ── Manual block / unblock (permanent, admin-only) ────────────────────
  const blockEmailManually = async () => {
    const email = manualBlockEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!window.confirm(
      `Permanently block ${email}?\n\n`
      + `They will not be able to sign in or register. Existing account (if any) can still generate content until logout — block a fingerprint or delete the account to lock them out immediately.`
    )) return;
    try {
      await axios.post(`${API}/admin/access/block-email`, {
        password, email, reason: manualBlockEmailReason.trim(),
      });
      toast.success(`${email} blocked.`);
      setManualBlockEmail('');
      setManualBlockEmailReason('');
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to block email');
    }
  };

  const unblockEmailManually = async (email) => {
    if (!email) return;
    if (!window.confirm(`Unblock ${email}? They can sign in / register again.`)) return;
    try {
      await axios.post(`${API}/admin/access/unblock-email`, { password, email });
      toast.success(`${email} unblocked.`);
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unblock email');
    }
  };

  const blockIpManually = async (ipArg) => {
    const ip = (ipArg || manualBlockIp).trim();
    if (!ip) { toast.error('Enter an IP'); return; }
    if (!window.confirm(
      `Permanently block IP ${ip}?\n\n`
      + `All future signups AND logins from this IP will be refused. Use for known credit farmers.\n\n`
      + `Different from "Clear this IP" — that only resets the 24h rate-limit ledger.`
    )) return;
    try {
      await axios.post(`${API}/admin/access/block-ip`, {
        password, ip, reason: manualBlockIpReason.trim(),
      });
      toast.success(`${ip} blocked.`);
      setManualBlockIp('');
      setManualBlockIpReason('');
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to block IP');
    }
  };

  const unblockIpManually = async (ip) => {
    if (!ip) return;
    if (!window.confirm(`Unblock IP ${ip}?`)) return;
    try {
      await axios.post(`${API}/admin/access/unblock-ip`, { password, ip });
      toast.success(`${ip} unblocked.`);
      await fetchAccessBlocks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unblock IP');
    }
  };

  // ── Blocked-device (fingerprint) admin actions ──
  const loadSuspiciousFingerprints = useCallback(async () => {
    setFpLoading(true);
    try {
      const res = await axios.post(`${API}/admin/fingerprints/suspicious`, {
        // hours: 0 → all-time (matches the lifetime cap enforced at signup).
        password, hours: 0, min_signups: 2,
      });
      setFpSuspicious(res.data.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load suspicious devices');
    } finally {
      setFpLoading(false);
    }
  }, [password]);

  const loadBlockedFingerprints = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/admin/fingerprints/blocked`, { password });
      setFpBlocked(res.data.blocked || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load blocked devices');
    }
  }, [password]);

  const blockFingerprint = async (fingerprint, reason) => {
    if (!fingerprint) return;
    if (!window.confirm(`Block this device? All future Google sign-ins from it will be rejected.\n\nFingerprint: ${fingerprint}`)) return;
    setFpBusy(true);
    try {
      await axios.post(`${API}/admin/fingerprints/block`, { password, fingerprint, reason });
      toast.success('Device blocked.');
      setFpManual('');
      await Promise.all([loadSuspiciousFingerprints(), loadBlockedFingerprints()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to block device');
    } finally {
      setFpBusy(false);
    }
  };

  const unblockFingerprint = async (fingerprint) => {
    if (!fingerprint) return;
    if (!window.confirm(`Unblock this device? Users on it will be able to sign in again.`)) return;
    setFpBusy(true);
    try {
      await axios.post(`${API}/admin/fingerprints/unblock`, { password, fingerprint });
      toast.success('Device unblocked.');
      await Promise.all([loadSuspiciousFingerprints(), loadBlockedFingerprints()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unblock device');
    } finally {
      setFpBusy(false);
    }
  };

  const fetchVideoPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const res = await axios.get(`${API}/video-pricing`);
      setPricingKlingSec1080(res.data.kling_per_sec_1080 ?? 25);
      setPricingKlingSec720(res.data.kling_per_sec_720 ?? 20);
      setPricingVeoSec1080(res.data.veo_per_sec_1080 ?? 75);
      setPricingVeoSec720(res.data.veo_per_sec_720 ?? 45);
      setPricingImg1080(res.data.img_1080p ?? 20);
      setPricingImg1440(res.data.img_1440p ?? 40);
      setPricingImg4k(res.data.img_4k ?? 60);
      setPricingOverlay(res.data.overlay ?? 40);
      setPricingTransform1080p(res.data.transform_1080p ?? 45);
      setPricingTransform4k(res.data.transform_4k ?? 70);
    } catch (err) {
      toast.error('Failed to load video pricing');
    } finally {
      setPricingLoading(false);
    }
  }, []);

  const saveVideoPricing = async () => {
    const ks1080 = parseInt(pricingKlingSec1080, 10);
    const ks720 = parseInt(pricingKlingSec720, 10);
    const vs1080 = parseInt(pricingVeoSec1080, 10);
    const vs720 = parseInt(pricingVeoSec720, 10);
    const i1080 = parseInt(pricingImg1080, 10);
    const i1440 = parseInt(pricingImg1440, 10);
    const i4k = parseInt(pricingImg4k, 10);
    const ov = parseInt(pricingOverlay, 10);
    const t1080 = parseInt(pricingTransform1080p, 10);
    const t4k = parseInt(pricingTransform4k, 10);
    // overlay/loop can be 0 (free); other fields still need to be positive.
    if (![ks1080, ks720, vs1080, vs720, i1080, i1440, i4k, t1080, t4k].every((n) => Number.isFinite(n) && n >= 1 && n <= 10000)) {
      toast.error('All values must be whole numbers between 1 and 10000');
      return;
    }
    if (!Number.isFinite(ov) || ov < 0 || ov > 10000) {
      toast.error('Overlay cost must be between 0 and 10000');
      return;
    }
    setPricingSaving(true);
    try {
      const res = await axios.post(`${API}/admin/video-pricing`, {
        password,
        kling_per_sec_1080: ks1080, kling_per_sec_720: ks720,
        veo_per_sec_1080: vs1080, veo_per_sec_720: vs720,
        img_1080p: i1080, img_1440p: i1440, img_4k: i4k,
        overlay: ov, transform_1080p: t1080, transform_4k: t4k,
      });
      setPricingKlingSec1080(res.data.kling_per_sec_1080);
      setPricingKlingSec720(res.data.kling_per_sec_720);
      setPricingVeoSec1080(res.data.veo_per_sec_1080);
      setPricingVeoSec720(res.data.veo_per_sec_720);
      setPricingImg1080(res.data.img_1080p);
      setPricingImg1440(res.data.img_1440p);
      setPricingImg4k(res.data.img_4k);
      setPricingOverlay(res.data.overlay);
      setPricingTransform1080p(res.data.transform_1080p);
      setPricingTransform4k(res.data.transform_4k);
      toast.success('Pricing updated — applies immediately to all new generations.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  const fetchDailyGenerations = useCallback(async (rangeDays) => {
    setDailyGenLoading(true);
    try {
      const res = await axios.post(`${API}/admin/daily-generations`, {
        password,
        days: rangeDays,
      });
      setDailyGenBuckets(res.data.buckets || []);
    } catch (err) {
      // Silent — Platform Health tab has other widgets; don't toast over them
    } finally {
      setDailyGenLoading(false);
    }
  }, [password]);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await axios.post(`${API}/admin/overview`, { password });
      setOverview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, [password]);

  const fetchTransactions = useCallback(async () => {
    setTxLoading(true);
    try {
      const res = await axios.post(`${API}/admin/transactions`, {
        password,
        tx_type: txFilterType === 'all' ? null : txFilterType,
        status: txFilterStatus === 'all' ? null : txFilterStatus,
        email_search: txEmailSearch.trim() || null,
        limit: TX_PAGE_SIZE,
        skip: txPage * TX_PAGE_SIZE,
      });
      setTxs(res.data.transactions || []);
      setTxSummary(res.data.summary || null);
      setTxTotalCount(res.data.total_count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setTxLoading(false);
    }
  }, [password, txFilterType, txFilterStatus, txEmailSearch, txPage]);

  const fetchPayingUsers = useCallback(async () => {
    setPayLoading(true);
    try {
      const res = await axios.post(`${API}/admin/paying-users`, {
        password,
        scope: payScope,
        email_search: payEmailSearch.trim() || null,
        sort_by: paySortBy,
        sort_dir: paySortDir,
      });
      setPayRows(res.data.users || []);
      setPaySummary(res.data.summary || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load paying users');
    } finally {
      setPayLoading(false);
    }
  }, [password, payScope, payEmailSearch, paySortBy, paySortDir]);

  // --- Stripe diagnostic (paying-users tab) ---------------------------------
  const runStripeDiagnostic = async () => {
    const email = stripeDiagEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter an email'); return; }
    setStripeDiagLoading(true);
    setStripeDiagResult(null);
    try {
      const res = await axios.post(`${API}/admin/user/stripe-status`, { password, email });
      setStripeDiagResult(res.data);
      if (res.data.mismatches?.length) {
        toast.error(`${res.data.mismatches.length} mismatch(es) found`);
      } else {
        toast.success('DB and Stripe are in sync');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Diagnostic failed');
    } finally {
      setStripeDiagLoading(false);
    }
  };

  const runStripeReconcile = async () => {
    const email = stripeDiagEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter an email'); return; }
    if (!window.confirm(
      `Reconcile ${email} against Stripe?\n\n`
      + `This will:\n`
      + `  • Overwrite the DB stripe_subscription_id with Stripe's active one\n`
      + `  • Credit the user for every PAID Stripe invoice we haven't recorded\n\n`
      + `Idempotent — safe to re-run.`
    )) return;
    setStripeReconcileLoading(true);
    try {
      const res = await axios.post(`${API}/admin/user/reconcile-stripe`, {
        password, email, add_missed_credits: true, fix_subscription_id: true,
      });
      toast.success(`${res.data.credits_added} credits added, ${res.data.actions.length} action(s)`);
      // Re-run diagnostic to show the post-fix state
      await runStripeDiagnostic();
      // Refresh the paying-users table so the row picks up new values
      fetchPayingUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reconcile failed');
    } finally {
      setStripeReconcileLoading(false);
    }
  };

  const fetchReplicateConfig = useCallback(async () => {
    setRepLoading(true);
    try {
      const res = await axios.post(`${API}/admin/replicate/config`, { password });
      setRepApiToken(res.data.api_token_masked || '');
      setRepSigningKey(res.data.webhook_signing_key_masked || '');
      setRepWebhookBaseUrl(res.data.webhook_base_url || '');
      setRepWebhookUrl(res.data.webhook_url || '');
      setRepTokenSet(!!res.data.api_token_set);
      setRepSigningKeySet(!!res.data.webhook_signing_key_set);
      setRepApiTokenChanged(false);
      setRepSigningKeyChanged(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load Replicate config');
    } finally {
      setRepLoading(false);
    }
  }, [password]);

  const saveReplicateConfig = async () => {
    setRepSaving(true);
    try {
      const payload = { password, webhook_base_url: repWebhookBaseUrl };
      if (repApiTokenChanged) payload.api_token = repApiToken;
      if (repSigningKeyChanged) payload.webhook_signing_key = repSigningKey;
      await axios.post(`${API}/admin/replicate/save`, payload);
      toast.success('Replicate config saved.');
      await fetchReplicateConfig();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setRepSaving(false);
    }
  };

  const fetchMaintenance = useCallback(async () => {
    setMaintLoading(true);
    try {
      const res = await axios.post(`${API}/admin/maintenance/status`, { password });
      setMaintEnabled(!!res.data.enabled);
      setMaintMessage(res.data.message || '');
    } catch (err) {
      // silent — health tab also shows other widgets
    } finally {
      setMaintLoading(false);
    }
  }, [password]);

  const toggleMaintenance = async (nextEnabled) => {
    setMaintSaving(true);
    try {
      const res = await axios.post(`${API}/admin/maintenance`, {
        password,
        enabled: nextEnabled,
        message: maintMessage,
      });
      setMaintEnabled(!!res.data.enabled);
      setMaintMessage(res.data.message || '');
      toast.success(nextEnabled ? 'Maintenance mode ON — site is now offline for users.' : 'Maintenance mode OFF — site is live again.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to toggle maintenance');
    } finally {
      setMaintSaving(false);
    }
  };

  const saveMaintenanceMessage = async () => {
    setMaintSaving(true);
    try {
      await axios.post(`${API}/admin/maintenance`, {
        password,
        enabled: maintEnabled,
        message: maintMessage,
      });
      toast.success('Maintenance message saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save message');
    } finally {
      setMaintSaving(false);
    }
  };

  const fetchGenerationRequests = useCallback(async () => {
    setGenRequestsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/generation-requests?limit=200`, { password });
      setGenRequests(res.data.requests || []);
      setRequestsPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load generation requests');
    } finally {
      setGenRequestsLoading(false);
    }
  }, [password]);

  useEffect(() => {
    if (authenticated && activeTab === 'requests') {
      fetchGenerationRequests();
    }
  }, [authenticated, activeTab, fetchGenerationRequests]);

  // Ratings tab state — one row per user who rated the tool on the front page.
  const [ratings, setRatings] = useState([]);
  const [ratingsSummary, setRatingsSummary] = useState({ total: 0, average: 0, distribution: {} });
  const [ratingsLoading, setRatingsLoading] = useState(false);

  const fetchRatings = useCallback(async () => {
    setRatingsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/ratings`, { password });
      setRatings(res.data.ratings || []);
      setRatingsSummary({
        total: res.data.total || 0,
        average: res.data.average || 0,
        distribution: res.data.distribution || {},
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load ratings');
    } finally {
      setRatingsLoading(false);
    }
  }, [password]);

  useEffect(() => {
    if (authenticated && activeTab === 'ratings') {
      fetchRatings();
    }
  }, [authenticated, activeTab, fetchRatings]);

  // Generate-thumbnails state
  const [thumbLoading, setThumbLoading] = useState(false);
  const [thumbResult, setThumbResult] = useState(null);

  const handleGenerateThumbnails = async () => {
    setThumbLoading(true);
    setThumbResult(null);
    try {
      const res = await axios.post(`${API}/admin/examples/generate-thumbnails`, { password });
      setThumbResult({ ok: true, ...res.data });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Thumbnail generation failed';
      setThumbResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setThumbLoading(false);
    }
  };

  // Transform-examples thumbnail generation (Prompt Management tab).
  const [xformThumbLoading, setXformThumbLoading] = useState(false);
  const [xformThumbResult, setXformThumbResult] = useState(null);
  const handleGenerateTransformThumbnails = async (force = false) => {
    setXformThumbLoading(true);
    setXformThumbResult(null);
    try {
      const res = await axios.post(`${API}/admin/transform-examples/generate-thumbnails`, { password, force });
      setXformThumbResult({ ok: true, ...res.data });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Transform thumbnail generation failed';
      setXformThumbResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setXformThumbLoading(false);
    }
  };

  const handleForceClaim = async (em) => {
    setForceClaimLoading(true);
    setForceClaimResult(null);
    try {
      const res = await axios.post(`${API}/admin/gifts/force-claim`, {
        password,
        email: em.trim().toLowerCase(),
      });
      setForceClaimResult({ ok: true, ...res.data });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Force-claim failed';
      setForceClaimResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setForceClaimLoading(false);
    }
  };

  const handleRestoreEmail = async (em) => {
    setRestoreLoading(true);
    setRestoreResult(null);
    try {
      const res = await axios.post(`${API}/admin/restore-email`, {
        password,
        email: em.trim().toLowerCase(),
      });
      setRestoreResult({ ok: true, ...res.data });
      toast.success(res.data.message);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Restore failed';
      setRestoreResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleAdjustCredits = async () => {
    const amt = parseInt(adjustAmount, 10);
    if (!adjustEmail.trim() || !Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid email and a positive amount.');
      return;
    }
    const confirmMsg =
      `Deduct ${amt} credits from ${adjustEmail.trim()} (bucket: ${adjustBucket})?\n\n` +
      (adjustCancelSub
        ? '⚠️ Also CANCEL this user\'s Stripe subscription immediately and downgrade them to free.\n\n'
        : '') +
      'This does NOT refund Stripe automatically — refund the payment in the Stripe dashboard separately.';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    setAdjustLoading(true);
    setAdjustResult(null);
    try {
      const res = await axios.post(`${API}/admin/adjust-credits`, {
        password,
        email: adjustEmail.trim().toLowerCase(),
        amount: amt,
        bucket: adjustBucket,
        reason: adjustReason.trim() || null,
        cancel_subscription: adjustCancelSub,
      });
      setAdjustResult({ ok: true, ...res.data });
      toast.success(res.data.message);
      // Clear inputs on success so a second refund doesn't accidentally reuse them
      setAdjustAmount('');
      setAdjustReason('');
      setAdjustCancelSub(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Credit deduction failed';
      setAdjustResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setAdjustLoading(false);
    }
  };

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await axios.post(`${API}/admin/users`, { password });
      setUsers(res.data.users || []);
    } catch {} finally { setUsersLoading(false); }
  }, [password]);

  // Format any ISO/timestamp string into Madrid local time.
  // Falls back to raw string if parsing fails.
  const formatMadrid = (ts) => {
    if (!ts) return '—';
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      return d.toLocaleString('en-GB', {
        timeZone: 'Europe/Madrid',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return ts; }
  };

  const fetchErrorLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/error-logs?level=${logFilter}&limit=100`, { password });
      setErrorLogs(res.data.logs || []);
    } catch {} finally { setLogsLoading(false); }
  }, [password, logFilter]);

  const fetchGifts = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/admin/gifts/list`, { password });
      setGifts(res.data.gifts || []);
    } catch {}
  }, [password]);

  const fetchPlatformUsage = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/admin/platform-usage`, { password });
      setPlatformUsage(res.data);
      setDailyVideoLimit(res.data.limits.daily_video_limit);
      setDailyImageLimit(res.data.limits.daily_image_limit);
    } catch {
      // silently fail
    }
  }, [password]);

  useEffect(() => {
    if (authenticated && activeTab === 'tutorials') {
      fetchTutorials();
      fetchTutorialsHeader();
    }
  }, [authenticated, activeTab, fetchTutorials, fetchTutorialsHeader]);

  useEffect(() => {
    if (authenticated && activeTab === 'access') {
      fetchAccessBlocks();
      loadSuspiciousFingerprints();
      loadBlockedFingerprints();
    }
  }, [authenticated, activeTab, fetchAccessBlocks, loadSuspiciousFingerprints, loadBlockedFingerprints]);

  useEffect(() => {
    if (authenticated && activeTab === 'pricing') {
      fetchVideoPricing();
    }
  }, [authenticated, activeTab, fetchVideoPricing]);

  useEffect(() => {
    if (authenticated && activeTab === 'replicate') {
      fetchReplicateConfig();
    }
  }, [authenticated, activeTab, fetchReplicateConfig]);

  useEffect(() => {
    if (authenticated && activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [authenticated, activeTab, fetchTransactions]);

  useEffect(() => {
    if (authenticated && activeTab === 'paying') {
      fetchPayingUsers();
    }
  }, [authenticated, activeTab, fetchPayingUsers]);

  // Overview KPIs refresh every 30s while on the dashboard
  useEffect(() => {
    if (authenticated && activeTab === 'overview') {
      fetchOverview();
      fetchDailyGenerations(dailyGenRange);
      const interval = setInterval(fetchOverview, 30000);
      return () => clearInterval(interval);
    }
  }, [authenticated, activeTab, fetchOverview, fetchDailyGenerations, dailyGenRange]);

  // Reset paging when filters/sort change
  useEffect(() => { setPayPage(0); }, [payScope, payEmailSearch, paySortBy, paySortDir]);

  // Auto-refresh platform usage every 10s when on health tab
  useEffect(() => {
    if (authenticated && activeTab === 'health') {
      fetchPlatformUsage();
      fetchMaintenance();
      fetchDailyGenerations(dailyGenRange);
      const interval = setInterval(fetchPlatformUsage, 10000);
      return () => clearInterval(interval);
    }
  }, [authenticated, activeTab, fetchPlatformUsage, fetchMaintenance, fetchDailyGenerations, dailyGenRange]);

  const handleSaveLimits = async () => {
    setSavingLimits(true);
    try {
      await axios.post(`${API}/admin/update-limits`, {
        password,
        daily_video_limit: dailyVideoLimit,
        daily_image_limit: dailyImageLimit
      });
      toast.success('Limits updated instantly');
      fetchPlatformUsage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update limits');
    } finally {
      setSavingLimits(false);
    }
  };

  // Analytics state
  const [gaMeasurementId, setGaMeasurementId] = useState('');
  const [savingAnalytics, setSavingAnalytics] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/admin/analytics`, { password });
      setGaMeasurementId(res.data.ga_measurement_id || '');
    } catch {}
  }, [password]);

  useEffect(() => {
    if (authenticated && activeTab === 'analytics') {
      fetchAnalytics();
    }
  }, [authenticated, activeTab, fetchAnalytics]);

  useEffect(() => {
    if (authenticated && activeTab === 'gifts') {
      fetchGifts();
    }
  }, [authenticated, activeTab, fetchGifts]);

  useEffect(() => {
    if (authenticated && activeTab === 'logs') {
      fetchErrorLogs();
      const interval = setInterval(fetchErrorLogs, 15000);
      return () => clearInterval(interval);
    }
  }, [authenticated, activeTab, fetchErrorLogs]);

  useEffect(() => {
    if (authenticated && activeTab === 'users') {
      fetchUsers();
    }
  }, [authenticated, activeTab, fetchUsers]);

  const handleCreateGift = async (e) => {
    e.preventDefault();
    setGiftLoading(true);
    try {
      const res = await axios.post(`${API}/admin/gifts`, {
        password,
        email: giftEmail,
        credits: giftCredits,
        expires_in_days: giftExpiry
      });
      toast.success(res.data.message);
      setGiftEmail('');
      setCheckedUser(null);
      fetchGifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gift');
    } finally {
      setGiftLoading(false);
    }
  };

  const handleCheckUser = async (email) => {
    setCheckLoading(true);
    try {
      const res = await axios.post(`${API}/admin/check-user?password=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`);
      setCheckedUser(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'User not found');
      setCheckedUser(null);
    } finally {
      setCheckLoading(false);
    }
  };

  const handleDeleteGift = async (giftId) => {
    setDeletingGift(giftId);
    try {
      await axios.delete(`${API}/admin/gifts/${giftId}?password=${encodeURIComponent(password)}`);
      toast.success('Gift deleted');
      fetchGifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete gift');
    } finally {
      setDeletingGift(null);
    }
  };

  const handleSaveAnalytics = async () => {
    setSavingAnalytics(true);
    try {
      await axios.post(`${API}/admin/update-analytics`, {
        password,
        ga_measurement_id: gaMeasurementId
      });
      toast.success('Analytics configuration saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save analytics');
    } finally {
      setSavingAnalytics(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/auth`, { password });
      setAuthenticated(true);
      toast.success('Access granted');
      loadConfig();
    } catch (error) {
      toast.error('Invalid admin password');
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/load-config`, { password });
      const data = response.data;
      setConfig(data);

      // Populate form fields
      setProjectNumber(data.google_vertex_ai?.project_number || '');
      setSaJsonPreview(data.google_vertex_ai?.service_account_json_preview || null);

      setBucketName(data.storage?.bucket_name || '');
      setBucketPath(data.storage?.bucket_path || 'outputs/');
      setVideoExpiryDays(data.storage?.video_expiry_days || 7);

      const prompt = data.video_generation?.default_prompt || {};
      setPromptText(prompt.prompt_text || '');
      setDefaultAnimate(prompt.default_animate || '');
      const veoPrompt = data.video_generation?.veo_prompt || {};
      setVeoPromptText(veoPrompt.prompt_text || '');
      setVeoDefaultAnimate(veoPrompt.default_animate || '');

      // Bypass keywords: backend returns list[str] OR string — we display
      // one keyword per line in the textarea.
      const rawBypass = data.video_generation?.bypass_keywords;
      if (Array.isArray(rawBypass)) {
        setBypassKeywords(rawBypass.join('\n'));
      } else if (typeof rawBypass === 'string') {
        setBypassKeywords(rawBypass);
      } else {
        setBypassKeywords('');
      }

      // Image Transformation prompt management
      const itc = data.image_transformation || {};
      setTransformSeasonPrefixes({
        summer: itc.season_prefixes?.summer || '',
        winter: itc.season_prefixes?.winter || '',
        autumn: itc.season_prefixes?.autumn || '',
        spring: itc.season_prefixes?.spring || '',
      });
      setTransformDaytimePrefixes({
        morning: itc.daytime_prefixes?.morning || '',
        afternoon: itc.daytime_prefixes?.afternoon || '',
        evening: itc.daytime_prefixes?.evening || '',
        late_night: itc.daytime_prefixes?.late_night || '',
      });
      setTransformWeatherPrefixes({
        rainfall: itc.weather_prefixes?.rainfall || '',
        snowfall: itc.weather_prefixes?.snowfall || '',
        overcast: itc.weather_prefixes?.overcast || '',
        clear_sky: itc.weather_prefixes?.clear_sky || '',
      });
      setTransformResolutionPrefixes({
        '1080p': itc.resolution_prefixes?.['1080p'] || '',
        '4k': itc.resolution_prefixes?.['4k'] || '',
      });
      setTransformMasterTemplate(itc.master_template || '');
      setTransformDescriptionPlaceholder(itc.custom_description_placeholder || '');

      // Rain Canvas masking prompts — show current defaults when unset so the
      // admin always sees the effective prompts.
      const rc = data.rain_canvas || {};
      setRainInteriorPrompts(rc.interior_prompts || RAIN_DEFAULT_INTERIOR);
      setRainOutdoorsPrompts(rc.outdoors_prompts || RAIN_DEFAULT_OUTDOORS);

      setStripeKey(data.general?.stripe_api_key || '');
      setStripeWebhookSecret(data.general?.stripe_webhook_secret || '');
      setStripeKeyChanged(false);
      setStripeWebhookChanged(false);
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedConfig = {
        google_vertex_ai: {
          project_number: projectNumber,
          service_account_json: saChanged ? serviceAccountJson : '••••••••'
        },
        storage: {
          bucket_name: bucketName,
          bucket_path: bucketPath,
          video_expiry_days: videoExpiryDays
        },
        video_generation: {
          default_prompt: {
            prompt_text: promptText,
            default_animate: defaultAnimate
          },
          veo_prompt: {
            prompt_text: veoPromptText,
            default_animate: veoDefaultAnimate
          },
          // Normalize textarea content → list[str] of trimmed non-empty lines.
          // Backend also accepts the raw string but the canonical form is a list.
          bypass_keywords: bypassKeywords
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          technical_settings: {
            sample_count: 1
          }
        },
        image_transformation: {
          season_prefixes: transformSeasonPrefixes,
          daytime_prefixes: transformDaytimePrefixes,
          weather_prefixes: transformWeatherPrefixes,
          resolution_prefixes: transformResolutionPrefixes,
          master_template: transformMasterTemplate,
          custom_description_placeholder: transformDescriptionPlaceholder,
        },
        rain_canvas: {
          interior_prompts: rainInteriorPrompts.trim(),
          outdoors_prompts: rainOutdoorsPrompts.trim(),
        },
        general: {
          video_expiry_days: videoExpiryDays,
          stripe_api_key: stripeKeyChanged ? stripeKey : '••••••••',
          stripe_webhook_secret: stripeWebhookChanged ? stripeWebhookSecret : '••••••••'
        }
      };

      const response = await axios.post(`${API}/admin/save-config`, {
        password,
        config: updatedConfig
      });

      toast.success(response.data.message);
      setSaChanged(false);
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/admin/test-vertex-connection`, { password });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const tabGroups = [
    { title: null, items: [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    ]},
    { title: 'Monitoring', items: [
      { id: 'health', label: 'Platform Health', icon: Activity },
      { id: 'logs', label: 'Error Logs', icon: AlertTriangle },
      { id: 'requests', label: 'Generation Requests', icon: Clock },
      { id: 'ratings', label: 'Ratings', icon: Star },
    ]},
    { title: 'Revenue', items: [
      { id: 'paying', label: 'Paying Users', icon: TrendingUp },
      { id: 'transactions', label: 'Transactions', icon: Receipt },
      { id: 'gifts', label: 'Gift Credits', icon: Gift },
    ]},
    { title: 'Users & Access', items: [
      { id: 'users', label: 'Users', icon: Users },
      { id: 'access', label: 'Access Control', icon: ShieldOff },
    ]},
    { title: 'Content & Config', items: [
      { id: 'pricing', label: 'Pricing', icon: DollarSign },
      { id: 'prompt', label: 'Default Prompt', icon: FileText },
      { id: 'transform_prompts', label: 'Prompt Management', icon: Wand2 },
      { id: 'rain_canvas', label: 'Rain Canvas', icon: CloudRain },
      { id: 'tutorials', label: 'Tutorials', icon: Play },
    ]},
    { title: 'Integrations', items: [
      { id: 'vertex', label: 'Vertex AI', icon: Key },
      { id: 'replicate', label: 'Replicate', icon: Video },
      { id: 'general', label: 'Stripe', icon: CreditCard },
      { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    ]},
  ];

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className="bg-[#1A2035] rounded-2xl p-8 border border-[#222D4A]">
            <div className="w-16 h-16 rounded-full bg-[#FFB238] flex items-center justify-center mx-auto mb-6">
              <Key className="w-8 h-8 text-[#0F1419]" />
            </div>
            <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Configuration</h1>
            <p className="text-gray-400 text-center mb-6">Enter password to manage app settings</p>

            <form onSubmit={handleAuth}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Admin password"
                className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none mb-4"
                data-testid="admin-password-input"
                autoFocus
              />
              <button
                type="submit"
                className="w-full px-6 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors"
                data-testid="admin-login-button"
              >
                Access Admin Panel
              </button>
            </form>

            <div className="mt-6 p-4 rounded-lg bg-[#222D4A] border border-[#2A3550]">
              <p className="text-sm text-gray-400">
                <span className="text-[#FFB238] font-medium">Hint:</span> Use the admin password configured in your backend settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1419] p-3 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Configuration</h1>
            <p className="text-gray-400 text-sm mt-1">Manage your Visual Atmos settings</p>
          </div>
          <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={loadConfig}
              disabled={loading}
              className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] transition-colors flex items-center justify-center gap-2 border border-[#2A3550] text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 sm:flex-initial px-3 sm:px-6 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="save-config-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* Mobile nav dropdown */}
        <div className="lg:hidden mb-4">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm font-medium"
            data-testid="admin-mobile-nav"
          >
            {tabGroups.map((group) => (
              <optgroup key={group.title || 'main'} label={group.title || 'Dashboard'}>
                {group.items.map((tab) => (
                  <option key={tab.id} value={tab.id}>{tab.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block lg:w-60 flex-shrink-0 sticky top-6" data-testid="admin-sidebar">
            <nav className="space-y-5">
              {tabGroups.map((group) => (
                <div key={group.title || 'main'}>
                  {group.title && (
                    <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {group.title}
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map((tab) => {
                      const Icon = tab.icon;
                      const active = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 text-sm font-medium transition-colors ${
                            active
                              ? 'bg-[#FFB238] text-[#0F1419]'
                              : 'text-gray-300 hover:text-white hover:bg-[#222D4A]'
                          }`}
                          data-testid={`tab-${tab.id}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          {/* Tab Content */}
          <div className="flex-1 min-w-0 bg-[#1A2035] rounded-2xl p-4 sm:p-8 border border-[#222D4A]">

          {/* ==================== OVERVIEW TAB ==================== */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="overview-tab-content">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Overview</h2>
                  <p className="text-sm text-gray-400">Your key numbers at a glance. Auto-refreshes every 30s.</p>
                </div>
                <button
                  onClick={fetchOverview}
                  disabled={overviewLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                  data-testid="overview-refresh-button"
                >
                  {overviewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {!overview ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-16 justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading dashboard…
                </div>
              ) : (
                <>
                  {/* Hero KPI row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="overview-hero">
                    {/* MRR */}
                    <div className="rounded-2xl p-5 bg-gradient-to-br from-[#FFB238]/15 to-[#222D4A] border border-[#FFB238]/40">
                      <div className="flex items-center gap-2 text-[#FFB238]">
                        <DollarSign className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Monthly Recurring Revenue</span>
                      </div>
                      <div className="text-4xl font-bold text-white mt-2 tabular-nums" data-testid="overview-mrr">${overview.mrr.toLocaleString()}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {overview.mrr_at_risk > 0
                          ? <span className="text-amber-400">${overview.mrr_at_risk.toLocaleString()}/mo at risk ({overview.cancellations_pending} cancelling)</span>
                          : <span>No cancellations pending</span>}
                      </div>
                    </div>
                    {/* Active subscribers */}
                    <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center gap-2 text-gray-300">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Active Subscribers</span>
                      </div>
                      <div className="text-4xl font-bold text-white mt-2 tabular-nums" data-testid="overview-active-subs">{overview.active_subscribers}</div>
                      <div className="flex gap-3 text-xs text-gray-400 mt-2">
                        <span>Starter <span className="text-white font-semibold">{overview.subscribers_by_tier.starter}</span></span>
                        <span>Plus <span className="text-white font-semibold">{overview.subscribers_by_tier.plus}</span></span>
                        <span>Pro <span className="text-white font-semibold">{overview.subscribers_by_tier.pro}</span></span>
                      </div>
                    </div>
                    {/* Total users */}
                    <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Users className="w-4 h-4 text-[#8AB4F8]" />
                        <span className="text-xs font-semibold uppercase tracking-wider">Total Users</span>
                      </div>
                      <div className="text-4xl font-bold text-white mt-2 tabular-nums" data-testid="overview-total-users">{overview.total_users.toLocaleString()}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        <UserPlus className="w-3 h-3 inline mr-1 text-green-400" />
                        +{overview.signups.today} today · +{overview.signups.last_7d} this week
                      </div>
                    </div>
                  </div>

                  {/* Secondary KPI row */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 uppercase tracking-wide"><UserPlus className="w-3.5 h-3.5" /> New signups (30d)</div>
                      <div className="text-2xl font-bold text-white mt-1 tabular-nums">{overview.signups.last_30d}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{overview.signups.last_7d} in last 7 days</div>
                    </div>
                    <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 uppercase tracking-wide"><Zap className="w-3.5 h-3.5" /> Generations today</div>
                      <div className="text-2xl font-bold text-[#FFB238] mt-1 tabular-nums" data-testid="overview-gens-today">{overview.generations_today.total}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{overview.generations_today.videos} vid · {overview.generations_today.loops} loop · {overview.generations_today.images} img</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${overview.success_rate_today >= 90 ? 'bg-[#222D4A] border-[#2A3550]' : 'bg-red-950/30 border-red-500/40'}`}>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 uppercase tracking-wide"><Percent className="w-3.5 h-3.5" /> Success rate today</div>
                      <div className={`text-2xl font-bold mt-1 tabular-nums ${overview.success_rate_today >= 90 ? 'text-green-400' : 'text-red-400'}`}>{overview.success_rate_today}%</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{overview.failures_today.total} failed · {overview.processing_now.videos + overview.processing_now.images} processing</div>
                    </div>
                    <div className={`rounded-xl p-4 border ${overview.errors_24h === 0 && overview.critical_24h === 0 ? 'bg-[#222D4A] border-[#2A3550]' : 'bg-red-950/30 border-red-500/40'}`}>
                      <div className="flex items-center gap-1.5 text-xs text-gray-400 uppercase tracking-wide"><AlertTriangle className="w-3.5 h-3.5" /> Errors (24h)</div>
                      <div className={`text-2xl font-bold mt-1 tabular-nums ${overview.errors_24h === 0 && overview.critical_24h === 0 ? 'text-green-400' : 'text-red-400'}`}>{overview.errors_24h}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{overview.critical_24h} critical</div>
                    </div>
                  </div>

                  {/* Credit liability */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Subscription credit liability</div>
                      <div className="text-2xl font-bold text-[#FFB238] mt-1 tabular-nums">{overview.credit_liability.subscription.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Resets at each renewal</div>
                    </div>
                    <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Top-up credit liability</div>
                      <div className="text-2xl font-bold text-red-400 mt-1 tabular-nums">{overview.credit_liability.topup.toLocaleString()}</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Never expire — open cost exposure</div>
                    </div>
                  </div>

                  {/* Two-column: Recent transactions + trend */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Recent transactions */}
                    <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]" data-testid="overview-recent-tx">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-[#FFB238]" />
                          <h3 className="text-base font-bold text-white">Latest transactions</h3>
                        </div>
                        <button onClick={() => setActiveTab('transactions')} className="text-xs text-[#FFB238] hover:underline">View all</button>
                      </div>
                      {overview.recent_transactions.length === 0 ? (
                        <div className="text-gray-500 text-sm py-6 text-center">No transactions yet.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {overview.recent_transactions.map((t, i) => (
                            <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-[#2A3550] last:border-0">
                              <div className="min-w-0">
                                <div className="text-sm text-white truncate">{t.email}</div>
                                <div className="text-[11px] text-gray-500 capitalize">{t.type}{t.plan_id ? ` · ${t.plan_id}` : ''} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-sm font-bold text-green-400 tabular-nums">${t.amount_paid.toFixed(2)}</div>
                                <div className="text-[11px] text-gray-500 tabular-nums">+{t.credits_added.toLocaleString()} cr</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Generations trend */}
                    <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-[#FFB238]" />
                          <h3 className="text-base font-bold text-white">Generations · last {dailyGenRange} days</h3>
                        </div>
                        <div className="flex bg-[#1A2035] rounded-lg p-1 border border-[#2A3550]">
                          {[7, 30].map((d) => (
                            <button key={d} onClick={() => setDailyGenRange(d)}
                              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dailyGenRange === d ? 'bg-[#FFB238] text-[#0F1419]' : 'text-gray-400 hover:text-white'}`}>
                              {d}d
                            </button>
                          ))}
                        </div>
                      </div>
                      <div style={{ width: '100%', height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={dailyGenBuckets.map((b) => {
                              const d = new Date(b.date + 'T00:00:00');
                              const label = dailyGenRange === 7
                                ? d.toLocaleDateString(undefined, { weekday: 'short' })
                                : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                              return { ...b, label };
                            })}
                            margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#2A3550" vertical={false} />
                            <XAxis dataKey="label" stroke="#6B7C9E" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#2A3550' }} interval={dailyGenRange === 30 ? 2 : 0} />
                            <YAxis stroke="#6B7C9E" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: '#2A3550' }} allowDecimals={false} />
                            <Tooltip contentStyle={{ backgroundColor: '#1A2035', border: '1px solid #2A3550', borderRadius: '8px', color: '#fff', fontSize: '12px' }} formatter={(value) => [value, 'Total']} cursor={{ fill: '#FFB238', fillOpacity: 0.06 }} />
                            <Bar dataKey="total" fill="#FFB238" radius={[4, 4, 0, 0]} maxBarSize={dailyGenRange === 30 ? 18 : 48} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ==================== PLATFORM HEALTH TAB ==================== */}
          {activeTab === 'health' && (
            <div className="space-y-8" data-testid="health-tab-content">
              {/* ============== MAINTENANCE MODE KILL SWITCH ============== */}
              <div
                className={`rounded-2xl p-5 border-2 transition-colors ${
                  maintEnabled
                    ? 'bg-red-950/40 border-red-500/60'
                    : 'bg-[#222D4A] border-[#2A3550]'
                }`}
                data-testid="maintenance-mode-card"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                        maintEnabled ? 'bg-red-500/20 text-red-400' : 'bg-[#1A2035] text-emerald-400'
                      }`}
                    >
                      <Power className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-white">Maintenance Mode</h3>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                            maintEnabled ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-400'
                          }`}
                          data-testid="maintenance-status-badge"
                        >
                          {maintEnabled ? 'Site Offline' : 'Live'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400 mt-1">
                        Flip this switch to take the public site offline. Users will see your message;
                        the admin panel stays accessible.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleMaintenance(!maintEnabled)}
                    disabled={maintSaving || maintLoading}
                    className={`px-4 py-2.5 rounded-lg font-bold flex items-center gap-2 transition-colors shrink-0 ${
                      maintEnabled
                        ? 'bg-emerald-500 hover:bg-emerald-400 text-[#0F1419]'
                        : 'bg-red-500 hover:bg-red-400 text-white'
                    } ${maintSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                    data-testid="maintenance-toggle-button"
                  >
                    {maintSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
                    {maintEnabled ? 'Bring Site Online' : 'Shut Site Down'}
                  </button>
                </div>

                <div className="mt-4">
                  <label className="block text-xs text-gray-400 mb-1 font-medium uppercase tracking-wide">
                    Message shown to users
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={maintMessage}
                      onChange={(e) => setMaintMessage(e.target.value)}
                      placeholder="We're performing scheduled maintenance. We'll be back shortly."
                      className="flex-1 px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                      data-testid="maintenance-message-input"
                    />
                    <button
                      onClick={saveMaintenanceMessage}
                      disabled={maintSaving}
                      className="px-4 py-2 rounded-lg bg-[#1A2035] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center justify-center gap-2"
                      data-testid="maintenance-save-message-button"
                    >
                      {maintSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save message
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Platform Usage & Health</h2>
                  <p className="text-sm text-gray-400">Real-time Vertex AI usage and generation limits. Auto-refreshes every 10 seconds.</p>
                </div>
                <button
                  onClick={fetchPlatformUsage}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] transition-colors flex items-center gap-2 border border-[#2A3550] text-sm"
                  data-testid="refresh-usage-button"
                >
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>

              {platformUsage ? (
                <>
                  {/* Key Metrics Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-5 rounded-xl bg-[#222D4A] border border-[#2A3550]">
                      <p className="text-gray-400 text-sm mb-1">Active Concurrent</p>
                      <p className="text-3xl font-bold text-white" data-testid="active-concurrent-metric">{platformUsage.usage.active_concurrent}</p>
                      <p className="text-xs text-gray-500 mt-1">jobs running now</p>
                    </div>
                    <div className="p-5 rounded-xl bg-[#222D4A] border border-[#2A3550]">
                      <p className="text-gray-400 text-sm mb-1">Failed Today</p>
                      <p className="text-3xl font-bold text-red-400" data-testid="failed-today-metric">
                        {platformUsage.usage.daily_failed_videos + platformUsage.usage.daily_failed_images}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{platformUsage.usage.daily_failed_videos} video / {platformUsage.usage.daily_failed_images} image</p>
                    </div>
                    <div className="p-5 rounded-xl bg-[#222D4A] border border-[#2A3550]">
                      <p className="text-gray-400 text-sm mb-1">Total Users</p>
                      <p className="text-3xl font-bold text-white" data-testid="total-users-metric">{platformUsage.total_users}</p>
                      <p className="text-xs text-gray-500 mt-1">registered accounts</p>
                    </div>
                  </div>

                  {/* All-Time Totals */}
                  {platformUsage.totals && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="p-4 rounded-xl bg-[#1A2035] border border-[#222D4A]">
                        <p className="text-gray-500 text-xs mb-1">Videos (All-Time)</p>
                        <p className="text-xl font-bold text-white">{platformUsage.totals.videos_all_time}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#1A2035] border border-[#222D4A]">
                        <p className="text-gray-500 text-xs mb-1">Images (All-Time)</p>
                        <p className="text-xl font-bold text-white">{platformUsage.totals.images_all_time}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#1A2035] border border-[#222D4A]">
                        <p className="text-gray-500 text-xs mb-1">Loops (All-Time)</p>
                        <p className="text-xl font-bold text-white">{platformUsage.totals.loops_all_time || 0}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-[#1A2035] border border-[#222D4A]">
                        <p className="text-gray-500 text-xs mb-1">Processing Now</p>
                        <p className="text-xl font-bold text-[#FFB238]">
                          {(platformUsage.totals.videos_processing || 0) + (platformUsage.totals.images_processing || 0)}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5">{platformUsage.totals.videos_processing} vid / {platformUsage.totals.images_processing} img</p>
                      </div>
                    </div>
                  )}

                  {/* Usage Progress Bars */}
                  <div className="space-y-6">
                    {/* Video Usage */}
                    {(() => {
                      const used = platformUsage.usage.daily_videos;
                      const limit = platformUsage.limits.daily_video_limit;
                      const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                      const isWarning = pct >= 80;
                      return (
                        <div data-testid="video-usage-bar">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">Today's Video Generations</span>
                            <span className={`font-bold ${isWarning ? 'text-red-400' : 'text-[#FFB238]'}`}>
                              {used} / {limit}
                            </span>
                          </div>
                          <div className="w-full h-4 bg-[#222D4A] rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${isWarning ? 'bg-red-500' : 'bg-[#FFB238]'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          {isWarning && (
                            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                              <ShieldAlert className="w-4 h-4" />
                              <span>Usage at {Math.round(pct)}% — consider increasing the limit!</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Image Usage */}
                    {(() => {
                      const used = platformUsage.usage.daily_images;
                      const limit = platformUsage.limits.daily_image_limit;
                      const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
                      const isWarning = pct >= 80;
                      return (
                        <div data-testid="image-usage-bar">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">Today's Image Generations</span>
                            <span className={`font-bold ${isWarning ? 'text-red-400' : 'text-[#FFB238]'}`}>
                              {used} / {limit}
                            </span>
                          </div>
                          <div className="w-full h-4 bg-[#222D4A] rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${isWarning ? 'bg-red-500' : 'bg-[#FFB238]'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          {isWarning && (
                            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                              <ShieldAlert className="w-4 h-4" />
                              <span>Usage at {Math.round(pct)}% — consider increasing the limit!</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Loop Usage (today) */}
                    <div data-testid="loop-usage-info">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">Today's Loop Conversions</span>
                        <span className="font-bold text-[#FFB238]">{platformUsage.usage.daily_loops || 0}</span>
                      </div>
                      <div className="w-full h-4 bg-[#222D4A] rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${Math.min((platformUsage.usage.daily_loops || 0) * 2, 100)}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Limit Controls */}
                  <div className="border-t border-[#222D4A] pt-6">
                    <h3 className="text-lg font-bold text-white mb-1">Daily Generation Limits</h3>
                    <p className="text-sm text-gray-400 mb-6">
                      Set to 0 to completely pause generation. Changes take effect instantly.
                    </p>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Daily Video Limit</label>
                        <input
                          type="number"
                          value={dailyVideoLimit}
                          onChange={(e) => setDailyVideoLimit(parseInt(e.target.value) || 0)}
                          min={0}
                          className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-lg font-mono"
                          data-testid="daily-video-limit-input"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-2">Daily Image Limit</label>
                        <input
                          type="number"
                          value={dailyImageLimit}
                          onChange={(e) => setDailyImageLimit(parseInt(e.target.value) || 0)}
                          min={0}
                          className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-lg font-mono"
                          data-testid="daily-image-limit-input"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleSaveLimits}
                      disabled={savingLimits}
                      className="mt-4 px-6 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2"
                      data-testid="save-limits-button"
                    >
                      {savingLimits ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Update Limits
                    </button>
                  </div>

                  {/* Kill Switch Info */}
                  <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-start gap-3">
                      <ShieldAlert className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-red-400 font-medium mb-1">Kill Switch</p>
                        <p className="text-sm text-gray-300">
                          Set either limit to <strong className="text-white">0</strong> to immediately pause all new generations of that type.
                          Existing in-progress jobs will finish. The 80% warning is logged to backend logs when usage reaches the threshold.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-[#FFB238] animate-spin mx-auto mb-3" />
                  <p className="text-gray-400">Loading platform stats...</p>
                </div>
              )}

              {/* ============== DAILY GENERATIONS CHART ============== */}
              <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]" data-testid="daily-generations-chart">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-[#FFB238]" />
                      <h3 className="text-lg font-bold text-white">Daily Generations</h3>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      Total generations per day (videos + loops + images). Today is the rightmost bar.
                    </p>
                  </div>
                  <div className="flex bg-[#1A2035] rounded-lg p-1 border border-[#2A3550]">
                    {[7, 30].map((d) => (
                      <button
                        key={d}
                        onClick={() => setDailyGenRange(d)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          dailyGenRange === d
                            ? 'bg-[#FFB238] text-[#0F1419]'
                            : 'text-gray-400 hover:text-white'
                        }`}
                        data-testid={`daily-gen-range-${d}`}
                      >
                        {d} days
                      </button>
                    ))}
                  </div>
                </div>

                {dailyGenLoading && dailyGenBuckets.length === 0 ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm py-12 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading chart…
                  </div>
                ) : (
                  <div style={{ width: '100%', height: 280 }} data-testid="daily-generations-svg-wrapper">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={dailyGenBuckets.map((b) => {
                          const d = new Date(b.date + 'T00:00:00');
                          const label = dailyGenRange === 7
                            ? d.toLocaleDateString(undefined, { weekday: 'short' })
                            : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          return { ...b, label };
                        })}
                        margin={{ top: 4, right: 8, left: -16, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#2A3550" vertical={false} />
                        <XAxis
                          dataKey="label"
                          stroke="#6B7C9E"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: '#2A3550' }}
                          interval={dailyGenRange === 30 ? 2 : 0}
                        />
                        <YAxis
                          stroke="#6B7C9E"
                          tick={{ fontSize: 11 }}
                          tickLine={false}
                          axisLine={{ stroke: '#2A3550' }}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1A2035',
                            border: '1px solid #2A3550',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '12px',
                          }}
                          labelFormatter={(_, payload) => {
                            const p = payload && payload[0] && payload[0].payload;
                            return p ? p.date : '';
                          }}
                          formatter={(value, name) => [value, name === 'total' ? 'Total' : name]}
                          cursor={{ fill: '#FFB238', fillOpacity: 0.06 }}
                        />
                        <Bar
                          dataKey="total"
                          fill="#FFB238"
                          radius={[4, 4, 0, 0]}
                          maxBarSize={dailyGenRange === 30 ? 18 : 48}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== ERROR LOGS TAB ==================== */}
          {activeTab === 'logs' && (
            <div className="space-y-4" data-testid="logs-tab-content">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Error Logs</h2>
                  <p className="text-sm text-gray-400">Recent warnings and errors from the backend. Auto-refreshes every 15s.</p>
                </div>
                <div className="flex items-center gap-3">
                  <select
                    value={logFilter}
                    onChange={(e) => setLogFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="log-filter-select"
                  >
                    <option value="ALL">All Levels</option>
                    <option value="WARNING">Warnings</option>
                    <option value="ERROR">Errors</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                  <button
                    onClick={fetchErrorLogs}
                    disabled={logsLoading}
                    className="px-4 py-2 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] hover:border-[#FFB238] transition-colors text-sm flex items-center gap-2"
                    data-testid="refresh-logs-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${logsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              {logsLoading && errorLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-[#FFB238] animate-spin mx-auto" />
                  <p className="text-gray-400 mt-3">Loading logs...</p>
                </div>
              ) : errorLogs.length === 0 ? (
                <div className="text-center py-12 bg-[#1A2035] rounded-xl border border-[#222D4A]">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-white font-medium">No errors or warnings</p>
                  <p className="text-gray-500 text-sm mt-1">Everything is running smoothly since last restart.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1" data-testid="logs-list">
                  {errorLogs.map((log, idx) => (
                    <div
                      key={`${log.timestamp}-${log.logger}-${idx}`}
                      className={`p-3 rounded-lg border font-mono text-xs ${
                        log.level === 'ERROR' || log.level === 'CRITICAL'
                          ? 'bg-red-500/10 border-red-500/30'
                          : 'bg-yellow-500/10 border-yellow-500/30'
                      }`}
                      data-testid={`log-entry-${idx}`}
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          log.level === 'ERROR' || log.level === 'CRITICAL'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}>{log.level}</span>
                        <span className="text-gray-500" data-testid={`log-timestamp-${idx}`}>{formatMadrid(log.timestamp)}</span>
                        <span className="text-gray-600">{log.logger}</span>
                      </div>
                      <p className="text-gray-300 break-all whitespace-pre-wrap leading-relaxed">{log.message}</p>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-600 text-center mt-2">
                Showing up to 100 most recent entries. Logs reset on server restart. Times in <span className="text-gray-400">Madrid time</span>.
              </p>
            </div>
          )}

          {/* ==================== GENERATION REQUESTS TAB ==================== */}
          {activeTab === 'requests' && (
            <div className="space-y-4" data-testid="requests-tab-content">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Generation Requests</h2>
                  <p className="text-sm text-gray-400">Video & image jobs. Duration is from request → delivery. Times in Madrid time.</p>
                </div>
                <button
                  onClick={fetchGenerationRequests}
                  disabled={genRequestsLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] hover:bg-[#2A3550] text-white border border-[#2A3550] flex items-center gap-2 disabled:opacity-50"
                  data-testid="refresh-requests-button"
                >
                  <RefreshCw className={`w-4 h-4 ${genRequestsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="bg-[#222D4A] rounded-xl border border-[#2A3550] overflow-x-auto">
                <table className="w-full text-sm" data-testid="requests-table">
                  <thead>
                    <tr className="border-b border-[#2A3550] text-left text-gray-400">
                      <th className="px-4 py-3 font-semibold">Requested</th>
                      <th className="px-4 py-3 font-semibold">User</th>
                      <th className="px-4 py-3 font-semibold">Type</th>
                      <th className="px-4 py-3 font-semibold">Model</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Duration</th>
                      <th className="px-4 py-3 font-semibold text-right">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {genRequestsLoading && genRequests.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                      </td></tr>
                    ) : genRequests.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No generation requests yet.</td></tr>
                    ) : (
                      genRequests
                        .slice((requestsPage - 1) * REQUESTS_PER_PAGE, requestsPage * REQUESTS_PER_PAGE)
                        .map((r) => (
                        <tr key={r.id} className="border-b border-[#2A3550] hover:bg-[#1A2035]/60" data-testid={`request-row-${r.id}`}>
                          <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleString('en-GB', { timeZone: 'Europe/Madrid', hour12: false })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-300 max-w-[180px] truncate">{r.user_email}</td>
                          <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#1A2035] text-gray-300 border border-[#2A3550]">{r.type}</span></td>
                          <td className="px-4 py-3 text-gray-300 font-mono text-xs">{r.engine || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              r.status === 'Completed' ? 'bg-green-500/15 text-green-400 border border-green-500/30' :
                              r.status === 'Failed' ? 'bg-red-500/15 text-red-400 border border-red-500/30' :
                              'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                            }`}>{r.status}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300 tabular-nums">
                            {r.duration_seconds != null ? `${r.duration_seconds.toFixed(1)} s` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300 tabular-nums">{r.credit_cost ?? '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination — 50 per page, up to 4 pages (last 200 jobs) */}
              {genRequests.length > REQUESTS_PER_PAGE && (
                <div className="flex items-center justify-between mt-3" data-testid="requests-pagination">
                  <span className="text-xs text-gray-500">
                    Showing {(requestsPage - 1) * REQUESTS_PER_PAGE + 1}–
                    {Math.min(requestsPage * REQUESTS_PER_PAGE, genRequests.length)} of {genRequests.length}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setRequestsPage((p) => Math.max(1, p - 1))}
                      disabled={requestsPage <= 1}
                      className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white text-sm border border-[#2A3550] hover:bg-[#2A3550] disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="requests-prev-page"
                    >
                      ← Prev
                    </button>
                    {Array.from(
                      { length: Math.ceil(genRequests.length / REQUESTS_PER_PAGE) },
                      (_, i) => i + 1
                    ).map((n) => (
                      <button
                        key={n}
                        onClick={() => setRequestsPage(n)}
                        className={`px-3 py-1.5 rounded-lg text-sm border ${
                          requestsPage === n
                            ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] font-bold'
                            : 'bg-[#222D4A] text-white border-[#2A3550] hover:bg-[#2A3550]'
                        }`}
                        data-testid={`requests-page-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() =>
                        setRequestsPage((p) =>
                          Math.min(Math.ceil(genRequests.length / REQUESTS_PER_PAGE), p + 1)
                        )
                      }
                      disabled={requestsPage >= Math.ceil(genRequests.length / REQUESTS_PER_PAGE)}
                      className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white text-sm border border-[#2A3550] hover:bg-[#2A3550] disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid="requests-next-page"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-600 text-center mt-2">
                Showing latest 200 jobs across videos + images, 50 per page. Duration only available for completed jobs created after the latest deploy.
              </p>
            </div>
          )}

          {/* ==================== RATINGS TAB ==================== */}
          {activeTab === 'ratings' && (
            <div className="space-y-4" data-testid="ratings-tab-content">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Tool Ratings</h2>
                  <p className="text-sm text-gray-400">Real ratings from the front-page “Rate this tool” widget. One row per user.</p>
                </div>
                <button
                  onClick={fetchRatings}
                  disabled={ratingsLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] hover:bg-[#2A3550] text-white border border-[#2A3550] flex items-center gap-2 disabled:opacity-50"
                  data-testid="refresh-ratings-button"
                >
                  <RefreshCw className={`w-4 h-4 ${ratingsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {/* Summary strip — real average + distribution */}
              <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
                <div className="bg-[#222D4A] rounded-xl p-4 border border-[#2A3550]" data-testid="ratings-summary-total">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Total Ratings</div>
                  <div className="text-2xl font-bold text-white mt-1">{ratingsSummary.total}</div>
                </div>
                <div className="bg-[#222D4A] rounded-xl p-4 border border-[#2A3550]" data-testid="ratings-summary-avg">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Real Average</div>
                  <div className="text-2xl font-bold text-[#FFB238] mt-1 flex items-center gap-1">
                    {ratingsSummary.average || '—'}
                    <Star className="w-5 h-5 fill-[#FFB238]" />
                  </div>
                </div>
                {[5, 4, 3, 2, 1].map((n) => (
                  <div key={n} className="bg-[#222D4A] rounded-xl p-4 border border-[#2A3550]" data-testid={`ratings-summary-star-${n}`}>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      {n} <Star className="w-3 h-3 fill-[#FFB238] text-[#FFB238]" />
                    </div>
                    <div className="text-2xl font-bold text-white mt-1">{ratingsSummary.distribution?.[n] ?? 0}</div>
                  </div>
                ))}
              </div>

              <div className="bg-[#222D4A] rounded-xl border border-[#2A3550] overflow-x-auto">
                <table className="w-full text-sm" data-testid="ratings-table">
                  <thead>
                    <tr className="border-b border-[#2A3550] text-left text-gray-400">
                      <th className="px-4 py-3 font-semibold">Email</th>
                      <th className="px-4 py-3 font-semibold text-center">Rating</th>
                      <th className="px-4 py-3 font-semibold">First Rated</th>
                      <th className="px-4 py-3 font-semibold">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ratingsLoading && ratings.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                      </td></tr>
                    ) : ratings.length === 0 ? (
                      <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No ratings yet.</td></tr>
                    ) : (
                      ratings.map((r, idx) => (
                        <tr key={idx} className="border-b border-[#2A3550] hover:bg-[#1A2035]/60" data-testid={`rating-row-${idx}`}>
                          <td className="px-4 py-3 text-gray-300">{r.email}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="inline-flex items-center gap-0.5" data-testid={`rating-stars-${idx}`}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                  key={n}
                                  className={`w-4 h-4 ${n <= (r.score || 0) ? 'fill-[#FFB238] text-[#FFB238]' : 'text-gray-600'}`}
                                />
                              ))}
                              <span className="ml-2 text-white font-medium tabular-nums">{r.score}/5</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                            {r.created_at
                              ? new Date(r.created_at).toLocaleString('en-GB', { timeZone: 'Europe/Madrid', hour12: false })
                              : '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                            {r.updated_at && r.updated_at !== r.created_at
                              ? new Date(r.updated_at).toLocaleString('en-GB', { timeZone: 'Europe/Madrid', hour12: false })
                              : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-600 text-center mt-2">
                “Real Average” shows the unpadded average across actual raters. The front-page widget shows a warm-start-adjusted average for social proof.
              </p>
            </div>
          )}

          {/* ==================== USERS TAB ==================== */}
          {activeTab === 'users' && (
            <div className="space-y-4" data-testid="users-tab-content">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Registered Users</h2>
                  <p className="text-sm text-gray-400">All accounts on the platform. Times in Madrid time.</p>
                </div>
                <button
                  onClick={fetchUsers}
                  disabled={usersLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] hover:bg-[#2A3550] text-white border border-[#2A3550] flex items-center gap-2 disabled:opacity-50"
                  data-testid="refresh-users-button"
                >
                  <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="bg-[#0F1419] rounded-lg border border-[#2A3550] overflow-hidden">
                <div className="px-4 py-3 border-b border-[#2A3550] flex items-center justify-between">
                  <span className="text-white font-medium">Total: {users.length} {users.length === 1 ? 'user' : 'users'}</span>
                </div>

                {usersLoading ? (
                  <div className="p-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#FFB238] mx-auto" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="p-12 text-center text-gray-500">No users registered yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="users-table">
                      <thead className="bg-[#1A2035] text-gray-400 text-xs uppercase">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Email</th>
                          <th className="px-4 py-3 text-left font-semibold">Registered (Madrid)</th>
                          <th className="px-4 py-3 text-left font-semibold">Auth</th>
                          <th className="px-4 py-3 text-left font-semibold">Tier</th>
                          <th className="px-4 py-3 text-right font-semibold">Credits</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u, idx) => (
                          <tr
                            key={u.id || idx}
                            className="border-t border-[#222D4A] hover:bg-[#1A2035] transition-colors"
                            data-testid={`user-row-${idx}`}
                          >
                            <td className="px-4 py-3 text-white break-all">{u.email}</td>
                            <td className="px-4 py-3 text-gray-300 whitespace-nowrap font-mono text-xs">{formatMadrid(u.created_at)}</td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded ${u.auth_provider === 'google' ? 'bg-blue-500/15 text-blue-400' : 'bg-gray-500/15 text-gray-400'}`}>
                                {u.auth_provider === 'google' ? 'Google' : 'Email'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded uppercase ${
                                u.subscription_tier === 'pro' ? 'bg-purple-500/15 text-purple-400' :
                                u.subscription_tier === 'plus' ? 'bg-[#FFB238]/15 text-[#FFB238]' :
                                u.subscription_tier === 'starter' ? 'bg-green-500/15 text-green-400' :
                                'bg-gray-500/15 text-gray-400'
                              }`}>
                                {u.subscription_tier}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-white font-medium">{u.credits_balance?.toLocaleString() || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ==================== VERTEX AI TAB ==================== */}
          {activeTab === 'vertex' && (
            <div className="space-y-6" data-testid="vertex-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Google Vertex AI Credentials</h2>
                <p className="text-sm text-gray-400">Connect your Google Cloud project for video generation using Veo 3.1</p>
              </div>

              {/* Status indicator */}
              {saJsonPreview && (
                <div className={`p-4 rounded-lg border ${
                  saJsonPreview.status === 'configured' 
                    ? 'bg-green-500/10 border-green-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}>
                  {saJsonPreview.status === 'configured' ? (
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <p className="text-green-400 font-medium">Credentials Configured</p>
                        <p className="text-sm text-green-400/70 mt-1">
                          Project: {saJsonPreview.project_id} | Email: {saJsonPreview.client_email}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                      <p className="text-yellow-400 font-medium">Credentials not configured yet</p>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-white font-medium mb-2">Google Cloud Project Number</label>
                <p className="text-sm text-gray-400 mb-3">
                  12-digit number found in Google Cloud Console, Project Settings
                </p>
                <input
                  type="text"
                  value={projectNumber}
                  onChange={(e) => setProjectNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  placeholder="123456789012"
                  maxLength={12}
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono"
                  data-testid="project-number-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Service Account JSON Key</label>
                <p className="text-sm text-gray-400 mb-3">
                  Paste the full JSON from your service account key file. Required roles: Vertex AI User, Storage Object Viewer.
                </p>
                <textarea
                  value={serviceAccountJson}
                  onChange={(e) => { setServiceAccountJson(e.target.value); setSaChanged(true); }}
                  placeholder={saJsonPreview?.status === 'configured' 
                    ? 'Credentials already saved. Paste new JSON to update...' 
                    : '{"type": "service_account", "project_id": "...", ...}'}
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                  rows={10}
                  data-testid="service-account-json-input"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="px-6 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                  data-testid="test-connection-button"
                >
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                  Test Connection
                </button>
              </div>
            </div>
          )}

          {/* ==================== TUTORIALS TAB ==================== */}
          {activeTab === 'tutorials' && (
            <div className="space-y-6" data-testid="tutorials-tab-content">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Tutorials</h2>
                  <p className="text-sm text-gray-400">
                    Curated YouTube videos shown on the public <code className="text-[#FFB238]">/tutorials</code> page.
                    The link is hidden from the nav until at least one is published.
                  </p>
                </div>
                <button
                  onClick={fetchTutorials}
                  disabled={tutLoading}
                  className="px-3 py-1.5 rounded-md bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                  data-testid="tutorials-refresh-button"
                >
                  {tutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {/* Page header editor (public hero copy on /tutorials) */}
              <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="tutorials-header-editor">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-white">Page header</h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Hero title and subtitle shown at the top of <code className="text-[#FFB238]">/tutorials</code>.
                    </p>
                  </div>
                </div>
                {tutHeaderLoading ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading current header…
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Title *</label>
                      <input
                        type="text"
                        value={tutHeader.title}
                        onChange={(e) => setTutHeader({ ...tutHeader, title: e.target.value })}
                        maxLength={200}
                        placeholder="Tutorials"
                        className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        data-testid="tutorials-header-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Subtitle</label>
                      <textarea
                        value={tutHeader.subtitle}
                        onChange={(e) => setTutHeader({ ...tutHeader, subtitle: e.target.value })}
                        maxLength={1000}
                        rows={3}
                        placeholder="Quick walkthroughs, tips and ideas…"
                        className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        data-testid="tutorials-header-subtitle-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">{(tutHeader.subtitle || '').length}/1000 chars. Line breaks preserved.</p>
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={fetchTutorialsHeader}
                        disabled={tutHeaderLoading}
                        className="px-3 py-1.5 rounded-md bg-[#1A2035] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                        data-testid="tutorials-header-refresh-button"
                      >
                        <RefreshCw className="w-4 h-4" /> Refresh
                      </button>
                      <button
                        onClick={saveTutorialsHeader}
                        disabled={tutHeaderSaving}
                        className="px-4 py-1.5 rounded-md bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
                        data-testid="tutorials-header-save-button"
                      >
                        {tutHeaderSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save header
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Add/edit form */}
              <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="tutorial-form">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-white">
                    {tutForm.id ? 'Edit tutorial' : 'Add a new tutorial'}
                  </h3>
                  {tutForm.id && (
                    <button
                      onClick={resetTutForm}
                      className="text-xs text-gray-400 hover:text-white"
                      data-testid="tutorial-form-cancel"
                    >
                      Cancel edit
                    </button>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Title *</label>
                  <input
                    type="text"
                    value={tutForm.title}
                    onChange={(e) => setTutForm({ ...tutForm, title: e.target.value })}
                    placeholder="Make Your First Cozy Loop in 60 Seconds"
                    className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="tutorial-title-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">YouTube URL *</label>
                  <input
                    type="text"
                    value={tutForm.url}
                    onChange={(e) => setTutForm({ ...tutForm, url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm font-mono"
                    data-testid="tutorial-url-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Accepts <code>youtube.com/watch?v=...</code>, <code>youtu.be/...</code>, <code>/shorts/...</code> or <code>/embed/...</code>.
                  </p>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Short description</label>
                  <textarea
                    value={tutForm.description}
                    onChange={(e) => setTutForm({ ...tutForm, description: e.target.value })}
                    placeholder="A 3-minute walkthrough of the loop tool."
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="tutorial-description-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <div>
                    <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Display order</label>
                    <input
                      type="number"
                      value={tutForm.display_order}
                      onChange={(e) => setTutForm({ ...tutForm, display_order: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                      data-testid="tutorial-order-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower number appears first.</p>
                  </div>
                  <label className="flex items-center gap-3 pb-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={tutForm.published}
                      onChange={(e) => setTutForm({ ...tutForm, published: e.target.checked })}
                      className="w-5 h-5 rounded accent-[#FFB238]"
                      data-testid="tutorial-published-checkbox"
                    />
                    <span className="text-sm text-white">Published <span className="text-gray-500">(visible on public page)</span></span>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={saveTutorial}
                    disabled={tutSaving}
                    className="px-5 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
                    data-testid="tutorial-save-button"
                  >
                    {tutSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {tutForm.id ? 'Update tutorial' : 'Add tutorial'}
                  </button>
                </div>
              </div>

              {/* Existing tutorials list */}
              <div className="rounded-2xl bg-[#222D4A] border border-[#2A3550] overflow-hidden">
                <div className="px-5 py-3 bg-[#1A2035] border-b border-[#2A3550]">
                  <h3 className="font-bold text-white">All tutorials ({tutList.length})</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Sorted by display order.</p>
                </div>
                {tutLoading && tutList.length === 0 ? (
                  <div className="px-5 py-10 text-center text-gray-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </div>
                ) : tutList.length === 0 ? (
                  <div className="px-5 py-10 text-center text-gray-500 text-sm" data-testid="tutorials-empty">
                    No tutorials yet — add your first one above.
                  </div>
                ) : (
                  <ul className="divide-y divide-[#2A3550]" data-testid="tutorials-list">
                    {tutList.map((t) => (
                      <li key={t.id} className="px-5 py-4 flex items-start gap-4 hover:bg-[#1A2035]/40">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-white truncate" title={t.title}>{t.title}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${t.published ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-gray-500/15 text-gray-400 border border-gray-500/30'}`}>
                              {t.published ? 'Published' : 'Draft'}
                            </span>
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide">order {t.display_order ?? 0}</span>
                          </div>
                          {t.description && (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{t.description}</p>
                          )}
                          <a href={t.url} target="_blank" rel="noreferrer" className="text-xs text-[#FFB238]/80 hover:text-[#FFB238] mt-1 inline-block truncate max-w-full">
                            {t.url}
                          </a>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => editTutorial(t)}
                            className="px-3 py-1.5 rounded-md bg-[#222D4A] text-white border border-[#2A3550] hover:bg-[#2A3550] text-xs"
                            data-testid={`tutorial-edit-${t.id}`}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteTutorial(t.id, t.title)}
                            className="px-3 py-1.5 rounded-md bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 text-xs flex items-center gap-1"
                            data-testid={`tutorial-delete-${t.id}`}
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* ==================== ACCESS CONTROL TAB ==================== */}
          {activeTab === 'access' && (
            <div className="space-y-8" data-testid="access-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Access Control</h2>
                <p className="text-sm text-gray-400">
                  Manual overrides for the two anti-abuse rules that can trip legitimate users:
                  <strong className="text-white"> IP rate limit</strong> (2 signups per IP per 24h) and
                  <strong className="text-white"> deleted-email block</strong> (prevents re-signup farming).
                </p>
              </div>

              {/* Manual unblock — quick action card */}
              <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550]" data-testid="access-manual-card">
                <div className="flex items-center gap-2 mb-3">
                  <Unlock className="w-5 h-5 text-[#FFB238]" />
                  <h3 className="text-lg font-bold text-white">Quick Unblock</h3>
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Got an IP or email from a customer's support email? Unblock without scrolling.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Clear IP rate limit</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={accManualIp}
                        onChange={(e) => setAccManualIp(e.target.value)}
                        placeholder="173.63.227.213"
                        className="flex-1 px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                        data-testid="access-manual-ip-input"
                      />
                      <button
                        onClick={() => clearIpBlock(accManualIp.trim())}
                        disabled={!accManualIp.trim()}
                        className="px-4 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                        data-testid="access-manual-ip-button"
                      >
                        Clear IP
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Restore deleted email</label>
                    <div className="flex gap-2">
                      <input
                        type="email"
                        value={accManualEmail}
                        onChange={(e) => setAccManualEmail(e.target.value)}
                        placeholder="user@gmail.com"
                        className="flex-1 px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        data-testid="access-manual-email-input"
                      />
                      <button
                        onClick={() => restoreDeletedEmail(accManualEmail.trim().toLowerCase())}
                        disabled={!accManualEmail.trim()}
                        className="px-4 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                        data-testid="access-manual-email-button"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Manual permanent block — email + IP */}
              <div className="rounded-2xl p-5 bg-[#1A2035] border border-red-500/30" data-testid="manual-block-card">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <h3 className="text-white font-bold text-base mb-1 flex items-center gap-2">
                      <ShieldOff className="w-4 h-4 text-red-400" />
                      Manual Permanent Block
                    </h3>
                    <p className="text-xs text-gray-400 max-w-3xl">
                      For credit-farmers slipping past the auto-heuristics. These blocks NEVER expire and apply to both new signups AND logins by existing users.
                      Different from "Clear this IP" (which only clears the 24h rate-limit) and from device fingerprints (which target the browser, not the network).
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Block Email */}
                  <div className="rounded-lg p-4 bg-[#0F1419] border border-[#2A3550]">
                    <label className="block text-xs text-gray-400 uppercase tracking-wide mb-2">Block Email Permanently</label>
                    <div className="space-y-2">
                      <input
                        type="email"
                        value={manualBlockEmail}
                        onChange={(e) => setManualBlockEmail(e.target.value)}
                        placeholder="farmer@gmail.com"
                        className="w-full px-3 py-2 rounded-md bg-[#1A2035] text-white border border-[#2A3550] focus:border-red-500 focus:outline-none text-sm"
                        data-testid="manual-block-email-input"
                      />
                      <input
                        type="text"
                        value={manualBlockEmailReason}
                        onChange={(e) => setManualBlockEmailReason(e.target.value)}
                        placeholder="Reason (optional, for audit trail)"
                        className="w-full px-3 py-2 rounded-md bg-[#1A2035] text-white border border-[#2A3550] focus:border-red-500 focus:outline-none text-xs"
                        data-testid="manual-block-email-reason"
                      />
                      <button
                        onClick={blockEmailManually}
                        disabled={!manualBlockEmail.trim()}
                        className="w-full px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
                        data-testid="manual-block-email-button"
                      >
                        Block This Email
                      </button>
                    </div>
                    {accBlockedEmails.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#2A3550]" data-testid="blocked-emails-list">
                        <div className="text-xs text-gray-500 mb-2">Currently blocked ({accBlockedEmails.length})</div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {accBlockedEmails.map((r) => (
                            <div key={r.email} className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded bg-[#1A2035]">
                              <div className="truncate flex-1">
                                <div className="text-white truncate" title={r.email}>{r.email}</div>
                                {r.reason && <div className="text-gray-500 text-[10px] truncate" title={r.reason}>{r.reason}</div>}
                              </div>
                              <button
                                onClick={() => unblockEmailManually(r.email)}
                                className="px-2 py-0.5 rounded bg-[#222D4A] text-gray-300 hover:bg-[#2A3550] text-[10px] whitespace-nowrap"
                                data-testid={`unblock-email-${r.email}`}
                              >
                                Unblock
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Block IP */}
                  <div className="rounded-lg p-4 bg-[#0F1419] border border-[#2A3550]">
                    <label className="block text-xs text-gray-400 uppercase tracking-wide mb-2">Block IP Permanently</label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={manualBlockIp}
                        onChange={(e) => setManualBlockIp(e.target.value)}
                        placeholder="173.63.227.213"
                        className="w-full px-3 py-2 rounded-md bg-[#1A2035] text-white border border-[#2A3550] focus:border-red-500 focus:outline-none font-mono text-sm"
                        data-testid="manual-block-ip-input"
                      />
                      <input
                        type="text"
                        value={manualBlockIpReason}
                        onChange={(e) => setManualBlockIpReason(e.target.value)}
                        placeholder="Reason (optional, for audit trail)"
                        className="w-full px-3 py-2 rounded-md bg-[#1A2035] text-white border border-[#2A3550] focus:border-red-500 focus:outline-none text-xs"
                        data-testid="manual-block-ip-reason"
                      />
                      <button
                        onClick={() => blockIpManually()}
                        disabled={!manualBlockIp.trim()}
                        className="w-full px-3 py-2 rounded-md bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
                        data-testid="manual-block-ip-button"
                      >
                        Block This IP
                      </button>
                    </div>
                    {accBlockedIps.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#2A3550]" data-testid="blocked-ips-list">
                        <div className="text-xs text-gray-500 mb-2">Currently blocked ({accBlockedIps.length})</div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {accBlockedIps.map((r) => (
                            <div key={r.ip} className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded bg-[#1A2035]">
                              <div className="truncate flex-1">
                                <div className="text-white font-mono truncate" title={r.ip}>{r.ip}</div>
                                {r.reason && <div className="text-gray-500 text-[10px] truncate" title={r.reason}>{r.reason}</div>}
                              </div>
                              <button
                                onClick={() => unblockIpManually(r.ip)}
                                className="px-2 py-0.5 rounded bg-[#222D4A] text-gray-300 hover:bg-[#2A3550] text-[10px] whitespace-nowrap"
                                data-testid={`unblock-ip-${r.ip}`}
                              >
                                Unblock
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Refresh */}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  Showing the {accIpRows.length} most recent of {accIpTotal} IP entries / {accDeletedRows.length} of {accDeletedTotal} deleted emails.
                </p>
                <button
                  onClick={fetchAccessBlocks}
                  disabled={accLoading}
                  className="px-3 py-1.5 rounded-md bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                  data-testid="access-refresh-button"
                >
                  {accLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {/* IP ledger */}
              <div className="rounded-2xl bg-[#222D4A] border border-[#2A3550] overflow-hidden">
                <div className="px-5 py-3 bg-[#1A2035] border-b border-[#2A3550] flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-bold text-white">Recent signups by IP</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Each row counts against the 2-per-24h limit for that IP.</p>
                  </div>
                  <input
                    type="text"
                    value={accIpSearch}
                    onChange={(e) => { setAccIpSearch(e.target.value); setAccIpPage(1); }}
                    placeholder="Filter by IP or email…"
                    className="px-3 py-1.5 rounded-md bg-[#0F1419] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm w-64"
                    data-testid="access-ip-search"
                  />
                </div>
                {accLoading && accIpRows.length === 0 ? (
                  <div className="px-5 py-10 text-center text-gray-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="access-ip-table">
                      <thead className="text-gray-400 uppercase text-xs tracking-wide">
                        <tr>
                          <th className="px-4 py-2.5 text-left">Created</th>
                          <th className="px-4 py-2.5 text-left">IP</th>
                          <th className="px-4 py-2.5 text-left">Email</th>
                          <th className="px-4 py-2.5 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#2A3550]">
                        {(() => {
                          const filtered = accIpRows.filter(r => {
                            if (!accIpSearch.trim()) return true;
                            const q = accIpSearch.toLowerCase();
                            return r.ip.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
                          });
                          const totalPages = Math.max(1, Math.ceil(filtered.length / ACC_IP_PER_PAGE));
                          const currentPage = Math.min(accIpPage, totalPages);
                          const start = (currentPage - 1) * ACC_IP_PER_PAGE;
                          const pageRows = filtered.slice(start, start + ACC_IP_PER_PAGE);
                          return pageRows.map((r) => {
                            const d = r.created_at ? new Date(r.created_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                            return (
                              <tr key={`${r.ip}-${r.email}-${r.created_at}`} className="hover:bg-[#1A2035]/50">
                                <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">{d}</td>
                                <td className="px-4 py-2.5 text-white font-mono text-xs">{r.ip}</td>
                                <td className="px-4 py-2.5 text-gray-300 truncate max-w-[280px]" title={r.email}>{r.email}</td>
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex justify-end gap-1.5">
                                    <button
                                      onClick={() => clearIpBlock(r.ip)}
                                      className="px-2.5 py-1 rounded-md bg-[#FFB238]/15 text-[#FFB238] border border-[#FFB238]/30 hover:bg-[#FFB238]/25 text-xs font-medium"
                                      data-testid={`access-clear-ip-${r.ip}`}
                                    >
                                      Clear
                                    </button>
                                    <button
                                      onClick={() => blockIpManually(r.ip)}
                                      className="px-2.5 py-1 rounded-md bg-red-600/15 text-red-400 border border-red-500/30 hover:bg-red-600/25 text-xs font-medium"
                                      data-testid={`access-block-ip-${r.ip}`}
                                      title="Permanently block this IP from signup/login"
                                    >
                                      Block
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                        {accIpRows.length === 0 && (
                          <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500 text-sm">No IP records.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* Pagination — 50 per page */}
                {(() => {
                  const filtered = accIpRows.filter(r => {
                    if (!accIpSearch.trim()) return true;
                    const q = accIpSearch.toLowerCase();
                    return r.ip.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
                  });
                  if (filtered.length <= ACC_IP_PER_PAGE) return null;
                  const totalPages = Math.ceil(filtered.length / ACC_IP_PER_PAGE);
                  const currentPage = Math.min(accIpPage, totalPages);
                  const start = (currentPage - 1) * ACC_IP_PER_PAGE;
                  const end = Math.min(start + ACC_IP_PER_PAGE, filtered.length);
                  return (
                    <div className="flex items-center justify-between px-5 py-3 border-t border-[#2A3550] bg-[#1A2035]/50 flex-wrap gap-3" data-testid="access-ip-pagination">
                      <span className="text-xs text-gray-500">
                        Showing {start + 1}–{end} of {filtered.length}
                      </span>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => setAccIpPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                          className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white text-sm border border-[#2A3550] hover:bg-[#2A3550] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="access-ip-prev-page"
                        >
                          ← Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                          <button
                            key={n}
                            onClick={() => setAccIpPage(n)}
                            className={`px-3 py-1.5 rounded-lg text-sm border ${
                              currentPage === n
                                ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238] font-bold'
                                : 'bg-[#222D4A] text-white border-[#2A3550] hover:bg-[#2A3550]'
                            }`}
                            data-testid={`access-ip-page-${n}`}
                          >
                            {n}
                          </button>
                        ))}
                        <button
                          onClick={() => setAccIpPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white text-sm border border-[#2A3550] hover:bg-[#2A3550] disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid="access-ip-next-page"
                        >
                          Next →
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Deleted-email block list */}
              <div className="rounded-2xl bg-[#222D4A] border border-[#2A3550] overflow-hidden">
                <div className="px-5 py-3 bg-[#1A2035] border-b border-[#2A3550] flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-bold text-white">Soft-deleted emails</h3>
                    <p className="text-xs text-gray-400 mt-0.5">These emails cannot sign up again until you restore them.</p>
                  </div>
                  <input
                    type="text"
                    value={accEmailSearch}
                    onChange={(e) => setAccEmailSearch(e.target.value)}
                    placeholder="Filter by email…"
                    className="px-3 py-1.5 rounded-md bg-[#0F1419] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm w-64"
                    data-testid="access-email-search"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="access-email-table">
                    <thead className="text-gray-400 uppercase text-xs tracking-wide">
                      <tr>
                        <th className="px-4 py-2.5 text-left">Deleted at</th>
                        <th className="px-4 py-2.5 text-left">Email</th>
                        <th className="px-4 py-2.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2A3550]">
                      {accDeletedRows
                        .filter(r => !accEmailSearch.trim() || r.email.toLowerCase().includes(accEmailSearch.toLowerCase()))
                        .map((r) => {
                          const d = r.deleted_at ? new Date(r.deleted_at).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                          return (
                            <tr key={r.email} className="hover:bg-[#1A2035]/50">
                              <td className="px-4 py-2.5 text-gray-300 whitespace-nowrap">{d}</td>
                              <td className="px-4 py-2.5 text-white">{r.email}</td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  onClick={() => restoreDeletedEmail(r.email)}
                                  className="px-3 py-1 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-medium"
                                  data-testid={`access-restore-email-${r.email}`}
                                >
                                  Restore
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      {accDeletedRows.length === 0 && (
                        <tr><td colSpan={3} className="px-4 py-10 text-center text-gray-500 text-sm">No soft-deleted emails.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ==================== BLOCKED DEVICES (fingerprint) ==================== */}
              <div className="rounded-2xl bg-[#222D4A] border border-[#2A3550] overflow-hidden" data-testid="access-fingerprints-card">
                <div className="p-5 border-b border-[#2A3550]">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldOff className="w-5 h-5 text-red-400" />
                    <h3 className="text-lg font-bold text-white">Blocked Devices</h3>
                  </div>
                  <p className="text-sm text-gray-400">
                    Fingerprint-based hard blocks. Any Google sign-in from a blocked device is rejected,
                    even if the user cycles VPN, changes email, or reinstalls the browser. Registration
                    from a fresh device is capped at{' '}
                    <strong className="text-white">2 accounts per device — lifetime, no reset</strong>.
                  </p>
                </div>

                {/* Suspicious device list */}
                <div className="p-5 border-b border-[#2A3550]">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-white">Farming candidates (all time)</p>
                      <p className="text-xs text-gray-500">Devices that have registered {'≥'}2 accounts ever.</p>
                    </div>
                    <button
                      onClick={() => loadSuspiciousFingerprints()}
                      disabled={fpLoading}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1A2035] border border-[#2A3550] hover:border-[#FFB238]/50 text-white text-xs font-medium"
                      data-testid="fp-refresh-suspicious"
                    >
                      {fpLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Refresh
                    </button>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-[#2A3550]">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1A2035] text-gray-400 uppercase text-xs">
                        <tr>
                          <th className="text-left px-3 py-2">Fingerprint</th>
                          <th className="text-left px-3 py-2">Signups</th>
                          <th className="text-left px-3 py-2">Emails</th>
                          <th className="text-left px-3 py-2">Last seen</th>
                          <th className="text-right px-3 py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#2A3550]">
                        {fpSuspicious.map((row) => (
                          <tr key={row.fingerprint} className="text-gray-200">
                            <td className="px-3 py-2 font-mono text-xs break-all" title={row.fingerprint}>
                              {row.fingerprint.slice(0, 12)}…
                            </td>
                            <td className="px-3 py-2 font-semibold">{row.signup_count}</td>
                            <td className="px-3 py-2 text-xs text-gray-300">
                              {row.emails.slice(0, 3).join(', ')}
                              {row.emails.length > 3 && <span className="text-gray-500"> +{row.emails.length - 3}</span>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {row.last_seen ? new Date(row.last_seen).toLocaleString() : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {row.is_blocked ? (
                                <span className="inline-flex items-center gap-1 text-xs text-red-300 font-medium">
                                  <ShieldOff className="w-3.5 h-3.5" /> Blocked
                                </span>
                              ) : (
                                <button
                                  onClick={() => blockFingerprint(row.fingerprint, `Farming: ${row.signup_count} signups`)}
                                  className="px-3 py-1 rounded-md bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/25 text-xs font-semibold"
                                  data-testid={`fp-block-${row.fingerprint.slice(0, 8)}`}
                                >
                                  Block device
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {fpSuspicious.length === 0 && !fpLoading && (
                          <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500 text-sm">No suspicious devices in the window.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Manual block card + currently-blocked list */}
                <div className="p-5">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">Block a specific fingerprint</p>
                      <p className="text-xs text-gray-500 mb-3">Paste the fingerprint id (or the short prefix from the table).</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={fpManual}
                          onChange={(e) => setFpManual(e.target.value)}
                          placeholder="e.g. 3f92a1c7e0…"
                          className="flex-1 px-3 py-2 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-xs"
                          data-testid="fp-manual-input"
                        />
                        <button
                          onClick={() => blockFingerprint(fpManual.trim(), 'Manual admin block')}
                          disabled={!fpManual.trim() || fpBusy}
                          className="px-4 py-2 rounded-lg bg-red-500/20 text-red-200 border border-red-500/40 hover:bg-red-500/30 text-sm font-semibold whitespace-nowrap disabled:opacity-50"
                          data-testid="fp-manual-block-button"
                        >
                          Block
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white mb-2">
                        Currently blocked ({fpBlocked.length})
                      </p>
                      <div className="rounded-lg border border-[#2A3550] max-h-56 overflow-y-auto divide-y divide-[#2A3550]">
                        {fpBlocked.map((row) => (
                          <div key={row.fingerprint} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                            <div className="min-w-0 flex-1">
                              <div className="font-mono text-gray-200 truncate" title={row.fingerprint}>
                                {row.fingerprint}
                              </div>
                              <div className="text-gray-500 mt-0.5 truncate">
                                {row.reason || 'Manual admin block'} · {row.blocked_at ? new Date(row.blocked_at).toLocaleDateString() : ''}
                              </div>
                            </div>
                            <button
                              onClick={() => unblockFingerprint(row.fingerprint)}
                              className="px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-medium whitespace-nowrap"
                              data-testid={`fp-unblock-${row.fingerprint.slice(0, 8)}`}
                            >
                              Unblock
                            </button>
                          </div>
                        ))}
                        {fpBlocked.length === 0 && (
                          <div className="px-3 py-8 text-center text-gray-500 text-xs">No blocked devices.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== VIDEO PRICING TAB ==================== */}
          {activeTab === 'pricing' && (
            <div className="space-y-6" data-testid="pricing-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Pricing</h2>
                <p className="text-sm text-gray-400">
                  Set the credit cost for each video and image tier. Changes apply immediately — both the
                  Generate page UI and the actual billing on every new generation will use these values
                  within ~5 seconds.
                </p>
              </div>

              {pricingLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading current prices…
                </div>
              ) : (
                <>
                  {/* Steady Vibe — Kling 3.0 (per-second) */}
                  <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550] space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-white">Steady Vibe — Kling 3.0</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Per-second rate. Users pick any duration 4–15s; cost = rate × seconds.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          1080p <span className="text-xs text-gray-500">(default 25/sec)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingKlingSec1080}
                            onChange={(e) => setPricingKlingSec1080(e.target.value)}
                            className="w-full pl-3 pr-20 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-kling-persec-1080-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">cr/sec</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">4s = {pricingKlingSec1080 * 4} · 15s = {pricingKlingSec1080 * 15} credits</p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          720p <span className="text-xs text-gray-500">(default 20/sec)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingKlingSec720}
                            onChange={(e) => setPricingKlingSec720(e.target.value)}
                            className="w-full pl-3 pr-20 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-kling-persec-720-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">cr/sec</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">4s = {pricingKlingSec720 * 4} · 15s = {pricingKlingSec720 * 15} credits</p>
                      </div>
                    </div>
                  </div>

                  {/* Superior Motion — Seedance 2.0 (per-second) */}
                  <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550] space-y-5">
                    <div>
                      <h3 className="text-base font-bold text-white">Superior Motion — Seedance 2.0</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Per-second rate. Users pick any duration 4–15s; cost = rate × seconds.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          1080p <span className="text-xs text-gray-500">(default 75/sec)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingVeoSec1080}
                            onChange={(e) => setPricingVeoSec1080(e.target.value)}
                            className="w-full pl-3 pr-20 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-veo-persec-1080-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">cr/sec</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">4s = {pricingVeoSec1080 * 4} · 15s = {pricingVeoSec1080 * 15} credits</p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          720p <span className="text-xs text-gray-500">(default 45/sec)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingVeoSec720}
                            onChange={(e) => setPricingVeoSec720(e.target.value)}
                            className="w-full pl-3 pr-20 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-veo-persec-720-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">cr/sec</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">4s = {pricingVeoSec720 * 4} · 15s = {pricingVeoSec720 * 15} credits</p>
                      </div>
                    </div>
                  </div>

                  {/* Image Generation */}
                  <div className="rounded-2xl p-5 bg-[#222D4A] border border-[#2A3550] space-y-4">
                    <div>
                      <h3 className="text-base font-bold text-white">Image Generation</h3>
                      <p className="text-xs text-gray-400 mt-0.5">Still images. User picks 1080p, 1440p (2K) or 4K.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          1080p <span className="text-xs text-gray-500">(default 20)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingImg1080}
                            onChange={(e) => setPricingImg1080(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-img-1080p-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          1440p (2K) <span className="text-xs text-gray-500">(default 40)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingImg1440}
                            onChange={(e) => setPricingImg1440(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-img-1440p-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          4K <span className="text-xs text-gray-500">(default 60)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingImg4k}
                            onChange={(e) => setPricingImg4k(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-img-4k-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Services (Atmospheric Overlays + Transform Image) */}
                  <div className="rounded-xl p-4 bg-[#222D4A]/50 border border-[#2A3550]" data-testid="pricing-services-section">
                    <div className="text-sm text-white font-semibold mb-3 flex items-center gap-2">
                      <span>Other Services</span>
                      <span className="text-xs text-gray-500 font-normal">— per-render cost</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          Atmospheric Overlays <span className="text-xs text-gray-500">(default 40)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="0" max="10000"
                            value={pricingOverlay}
                            onChange={(e) => setPricingOverlay(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-overlay-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          Transform Image · 1080p <span className="text-xs text-gray-500">(default 45)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingTransform1080p}
                            onChange={(e) => setPricingTransform1080p(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-transform-1080p-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1.5">
                          Transform Image · 4K <span className="text-xs text-gray-500">(default 70)</span>
                        </label>
                        <div className="relative">
                          <input
                            type="number" min="1" max="10000"
                            value={pricingTransform4k}
                            onChange={(e) => setPricingTransform4k(e.target.value)}
                            className="w-full pl-3 pr-16 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-medium"
                            data-testid="pricing-transform-4k-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 uppercase tracking-wide pointer-events-none">credits</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={fetchVideoPricing}
                      disabled={pricingLoading}
                      className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                      data-testid="pricing-refresh-button"
                    >
                      <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button
                      onClick={saveVideoPricing}
                      disabled={pricingSaving}
                      className="px-5 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
                      data-testid="pricing-save-button"
                    >
                      {pricingSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save pricing
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ==================== TRANSACTIONS TAB ==================== */}
          {activeTab === 'paying' && (
            <div className="space-y-6" data-testid="paying-users-tab-content">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Paying Users — Credit Liability</h2>
                  <p className="text-sm text-gray-400 max-w-3xl">
                    Active subscribers + anyone holding non-expiring top-up credits. Subscription credits
                    <span className="text-[#FFB238] font-medium"> reset at every monthly renewal</span> — high unused balance on
                    renewal day = a profitable subscriber for you. Top-up credits never expire, so they
                    represent an open generation-cost liability until spent.
                  </p>
                </div>
                <button
                  onClick={fetchPayingUsers}
                  disabled={payLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                  data-testid="paying-users-refresh-button"
                >
                  {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {/* Summary tiles */}
              {paySummary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="paying-users-summary">
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Paying users</div>
                    <div className="text-2xl font-bold text-white mt-1">{paySummary.user_count}</div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Subscription credits</div>
                    <div className="text-2xl font-bold text-[#FFB238] mt-1">{paySummary.total_subscription_credits.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Will reset at next renewal</div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Top-up liability</div>
                    <div className="text-2xl font-bold text-red-400 mt-1">{paySummary.total_topup_credits.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">Never expire</div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Lifetime revenue</div>
                    <div className="text-2xl font-bold text-green-400 mt-1">${paySummary.total_lifetime_spent_usd.toLocaleString()}</div>
                  </div>
                </div>
              )}

              {/* Stripe Diagnostic + Reconcile ------------------------------ */}
              <div className="rounded-2xl p-5 bg-[#1A2035] border border-[#FFB238]/30" data-testid="stripe-diag-card">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <h3 className="text-white font-bold text-base mb-1">Stripe Diagnostic + Reconcile</h3>
                    <p className="text-xs text-gray-400 max-w-3xl">
                      Pulls the full Stripe picture for a user (subscriptions, invoices, charges) and flags any DB drift.
                      If Stripe has paid invoices we haven't credited (missed renewal webhooks), Reconcile will grant them retroactively.
                      Idempotent — safe to re-run.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="email"
                    value={stripeDiagEmail}
                    onChange={(e) => setStripeDiagEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="flex-1 min-w-[240px] px-3 py-2 rounded-md bg-[#0F1419] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="stripe-diag-email"
                  />
                  <button
                    onClick={runStripeDiagnostic}
                    disabled={stripeDiagLoading || !stripeDiagEmail.trim()}
                    className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                    data-testid="stripe-diag-run"
                  >
                    {stripeDiagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Check Stripe
                  </button>
                  <button
                    onClick={runStripeReconcile}
                    disabled={stripeReconcileLoading || !stripeDiagEmail.trim()}
                    className="px-4 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] hover:bg-[#FFC85C] text-sm font-bold flex items-center gap-2 disabled:opacity-50"
                    data-testid="stripe-diag-reconcile"
                  >
                    {stripeReconcileLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Reconcile
                  </button>
                </div>

                {stripeDiagResult && (
                  <div className="mt-4 space-y-3" data-testid="stripe-diag-result">
                    {/* Mismatches banner */}
                    {stripeDiagResult.mismatches?.length > 0 ? (
                      <div className="rounded-lg p-3 bg-red-950/40 border border-red-500/40" data-testid="stripe-diag-mismatches">
                        <div className="text-red-300 font-medium text-sm mb-1">Mismatches ({stripeDiagResult.mismatches.length})</div>
                        <ul className="list-disc list-inside text-xs text-red-200/90 space-y-0.5">
                          {stripeDiagResult.mismatches.map((m, i) => <li key={i}>{m}</li>)}
                        </ul>
                      </div>
                    ) : (
                      <div className="rounded-lg p-2 bg-green-950/30 border border-green-500/30 text-green-300 text-xs">
                        DB and Stripe are in sync — no mismatches.
                      </div>
                    )}
                    {/* Counts */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="rounded-md p-2 bg-[#0F1419] border border-[#2A3550]">
                        <div className="text-gray-500">DB tier</div>
                        <div className="text-white font-medium">{stripeDiagResult.db?.subscription_tier || 'free'}</div>
                      </div>
                      <div className="rounded-md p-2 bg-[#0F1419] border border-[#2A3550]">
                        <div className="text-gray-500">DB sub id</div>
                        <div className="text-white font-mono text-[10px] break-all">{stripeDiagResult.db?.stripe_subscription_id || '—'}</div>
                      </div>
                      <div className="rounded-md p-2 bg-[#0F1419] border border-[#2A3550]">
                        <div className="text-gray-500">Stripe active subs</div>
                        <div className="text-white font-medium">{stripeDiagResult.stripe?.active_subscription_ids?.length ?? 0}</div>
                      </div>
                      <div className="rounded-md p-2 bg-[#0F1419] border border-[#2A3550]">
                        <div className="text-gray-500">Paid inv / DB tx</div>
                        <div className="text-white font-medium">{stripeDiagResult.counts?.stripe_invoices_paid ?? 0} / {stripeDiagResult.counts?.db_transactions_completed ?? 0}</div>
                      </div>
                    </div>
                    {/* Subscriptions table */}
                    {stripeDiagResult.stripe?.subscriptions?.length > 0 && (
                      <div className="rounded-lg overflow-x-auto bg-[#0F1419] border border-[#2A3550]" data-testid="stripe-diag-subs">
                        <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-[#2A3550]">Subscriptions</div>
                        <table className="w-full text-xs">
                          <thead className="text-gray-500">
                            <tr>
                              <th className="text-left px-3 py-1.5">id</th>
                              <th className="text-left px-3 py-1.5">status</th>
                              <th className="text-left px-3 py-1.5">amount</th>
                              <th className="text-left px-3 py-1.5">current period</th>
                              <th className="text-left px-3 py-1.5">cancel@end</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#2A3550]">
                            {stripeDiagResult.stripe.subscriptions.map((s) => (
                              <tr key={s.id}>
                                <td className="px-3 py-1.5 text-white font-mono text-[10px] break-all">{s.id}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    s.status === 'active' ? 'bg-green-900/50 text-green-300' :
                                    s.status === 'canceled' ? 'bg-gray-700/50 text-gray-400' :
                                    'bg-yellow-900/50 text-yellow-300'
                                  }`}>{s.status}</span>
                                </td>
                                <td className="px-3 py-1.5 text-white">${s.amount}/{s.interval}</td>
                                <td className="px-3 py-1.5 text-gray-400 whitespace-nowrap">{(s.current_period_start || '').slice(0,10)} → {(s.current_period_end || '').slice(0,10)}</td>
                                <td className="px-3 py-1.5 text-gray-300">{s.cancel_at_period_end ? 'YES' : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {/* Invoices table */}
                    {stripeDiagResult.stripe?.invoices?.length > 0 && (
                      <div className="rounded-lg overflow-x-auto bg-[#0F1419] border border-[#2A3550]" data-testid="stripe-diag-invoices">
                        <div className="px-3 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-[#2A3550]">Invoices (Stripe)</div>
                        <table className="w-full text-xs">
                          <thead className="text-gray-500">
                            <tr>
                              <th className="text-left px-3 py-1.5">created</th>
                              <th className="text-left px-3 py-1.5">status</th>
                              <th className="text-left px-3 py-1.5">reason</th>
                              <th className="text-left px-3 py-1.5">total</th>
                              <th className="text-left px-3 py-1.5">paid</th>
                              <th className="text-left px-3 py-1.5">id</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#2A3550]">
                            {stripeDiagResult.stripe.invoices.map((i) => (
                              <tr key={i.id}>
                                <td className="px-3 py-1.5 text-gray-300 whitespace-nowrap">{(i.created || '').slice(0,10)}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    i.status === 'paid' ? 'bg-green-900/50 text-green-300' :
                                    i.status === 'draft' ? 'bg-blue-900/50 text-blue-300' :
                                    i.status === 'void' ? 'bg-gray-700/50 text-gray-500' :
                                    'bg-yellow-900/50 text-yellow-300'
                                  }`}>{i.status}</span>
                                </td>
                                <td className="px-3 py-1.5 text-gray-400">{i.billing_reason || '—'}</td>
                                <td className="px-3 py-1.5 text-white">${i.total}</td>
                                <td className="px-3 py-1.5 text-white">${i.amount_paid}</td>
                                <td className="px-3 py-1.5 text-white font-mono text-[10px] break-all">{i.hosted_invoice_url ? <a href={i.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#FFB238] hover:underline">{i.id}</a> : i.id}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="rounded-2xl p-4 bg-[#222D4A] border border-[#2A3550] flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2" data-testid="paying-users-scope">
                  {[
                    { key: 'all', label: 'All paying' },
                    { key: 'subscribers', label: 'Subscribers only' },
                    { key: 'topup', label: 'Top-up holders only' },
                  ].map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setPayScope(s.key)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        payScope === s.key
                          ? 'bg-[#FFB238] text-[#0F1419] border-[#FFB238]'
                          : 'bg-[#1A2035] text-gray-300 border-[#2A3550] hover:border-[#FFB238]/50'
                      }`}
                      data-testid={`paying-users-scope-${s.key}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={payEmailSearch}
                  onChange={(e) => setPayEmailSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') fetchPayingUsers(); }}
                  placeholder="Search email…"
                  className="px-3 py-1.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm flex-1 min-w-[200px]"
                  data-testid="paying-users-email-search"
                />
                <select
                  value={`${paySortBy}:${paySortDir}`}
                  onChange={(e) => {
                    const [by, dir] = e.target.value.split(':');
                    setPaySortBy(by);
                    setPaySortDir(dir);
                  }}
                  className="px-3 py-1.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] text-sm"
                  data-testid="paying-users-sort"
                >
                  <option value="renewal_at:asc">Sort: Soonest renewal first</option>
                  <option value="renewal_at:desc">Sort: Latest renewal first</option>
                  <option value="sub_credits:desc">Sort: Highest sub credits</option>
                  <option value="topup_credits:desc">Sort: Highest top-up credits</option>
                  <option value="lifetime_spent:desc">Sort: Highest lifetime spend</option>
                </select>
                <button
                  onClick={fetchPayingUsers}
                  className="px-4 py-1.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold text-sm hover:bg-[#FFDA82]"
                  data-testid="paying-users-apply-filters"
                >
                  Apply
                </button>
              </div>

              {/* Table */}
              <div className="rounded-2xl overflow-x-auto bg-[#1A2035] border border-[#2A3550]" data-testid="paying-users-table-wrap">
                {payLoading ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm p-6">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading paying users…
                  </div>
                ) : payRows.length === 0 ? (
                  <div className="text-gray-500 text-sm p-6">No paying users match this filter.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="paying-users-table">
                    <thead className="bg-[#222D4A] text-gray-400 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">Email</th>
                        <th className="text-left px-4 py-3">Tier</th>
                        <th className="text-right px-4 py-3 whitespace-nowrap">Sub credits<br/><span className="text-[10px] normal-case text-gray-500">(expires at renewal)</span></th>
                        <th className="text-right px-4 py-3 whitespace-nowrap">Top-up credits<br/><span className="text-[10px] normal-case text-gray-500">(never expire)</span></th>
                        <th className="text-left px-4 py-3 whitespace-nowrap">Next renewal</th>
                        <th className="text-right px-4 py-3 whitespace-nowrap">Lifetime spent</th>
                        <th className="text-left px-4 py-3 whitespace-nowrap">Last generation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payRows.slice(payPage * PAY_PAGE_SIZE, (payPage + 1) * PAY_PAGE_SIZE).map((r) => {
                        const fmtDate = (s) => {
                          if (!s) return '—';
                          try { return new Date(s).toLocaleDateString(); } catch { return '—'; }
                        };
                        const days = r.days_until_renewal;
                        const hrs = r.hours_until_renewal;
                        let renewalLabel;
                        if (days === null || days === undefined) {
                          renewalLabel = <span className="text-gray-500">—</span>;
                        } else {
                          const showHours = (hrs !== null && hrs !== undefined && hrs < 48);
                          const urgent = (hrs !== null && hrs !== undefined && hrs < 24);
                          let countdown;
                          if (showHours) {
                            countdown = hrs <= 0 ? 'today' : `${hrs}h left`;
                          } else {
                            countdown = `${days}d left`;
                          }
                          renewalLabel = (
                            <div className="flex flex-col leading-tight">
                              <span className={urgent ? 'text-red-400 font-semibold' : 'text-gray-300'}>{fmtDate(r.renewal_at)}</span>
                              <span className={`text-[10px] uppercase tracking-wide ${urgent ? 'text-red-400' : 'text-gray-500'}`}>
                                renews · {countdown}
                              </span>
                            </div>
                          );
                        }
                        return (
                          <tr key={r.id} className="border-t border-[#2A3550] hover:bg-[#222D4A]/40" data-testid="paying-users-row">
                            <td className="px-4 py-3 text-white">
                              <div className="font-medium" data-testid="paying-users-email">{r.email}</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                {r.has_active_subscription && <span className="text-[10px] uppercase tracking-wide text-green-400">active</span>}
                                {r.subscription_cancel_pending && <span className="text-[10px] uppercase tracking-wide text-amber-400">cancel pending</span>}
                                {r.pending_downgrade_to && <span className="text-[10px] uppercase tracking-wide text-amber-400">→ {r.pending_downgrade_to}</span>}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-300 capitalize">{r.subscription_tier}</td>
                            <td className="px-4 py-3 text-right text-[#FFB238] font-medium tabular-nums">{r.subscription_credits.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right text-red-400 font-medium tabular-nums">{r.topup_credits.toLocaleString()}</td>
                            <td className="px-4 py-3 text-gray-300 tabular-nums">{renewalLabel}</td>
                            <td className="px-4 py-3 text-right text-green-400 font-medium tabular-nums">${r.lifetime_spent_usd.toFixed(2)}</td>
                            <td className="px-4 py-3 text-gray-400 tabular-nums">{fmtDate(r.last_generation_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {payRows.length > PAY_PAGE_SIZE && (
                <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="paying-users-pagination">
                  <div className="text-xs text-gray-400">
                    Showing {payPage * PAY_PAGE_SIZE + 1}–{Math.min((payPage + 1) * PAY_PAGE_SIZE, payRows.length)} of {payRows.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPayPage((p) => Math.max(0, p - 1))}
                      disabled={payPage === 0}
                      className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] text-sm disabled:opacity-40 hover:bg-[#2A3550]"
                      data-testid="paying-users-prev-page"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-400 tabular-nums">
                      Page {payPage + 1} / {Math.ceil(payRows.length / PAY_PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => setPayPage((p) => (p + 1 < Math.ceil(payRows.length / PAY_PAGE_SIZE) ? p + 1 : p))}
                      disabled={(payPage + 1) * PAY_PAGE_SIZE >= payRows.length}
                      className="px-3 py-1.5 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] text-sm disabled:opacity-40 hover:bg-[#2A3550]"
                      data-testid="paying-users-next-page"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="space-y-6" data-testid="transactions-tab-content">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">Transactions</h2>
                  <p className="text-sm text-gray-400">All top-ups and subscriptions across the platform. Revenue counts completed payments only.</p>
                </div>
                <button
                  onClick={fetchTransactions}
                  disabled={txLoading}
                  className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                  data-testid="transactions-refresh-button"
                >
                  {txLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {/* Summary cards */}
              {txSummary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="transactions-summary">
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Revenue</div>
                    <div className="text-2xl font-bold text-emerald-400" data-testid="summary-revenue">
                      ${txSummary.total_revenue_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{txSummary.completed_count} completed</div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Credits Sold</div>
                    <div className="text-2xl font-bold text-[#FFB238]" data-testid="summary-credits-sold">
                      {txSummary.total_credits_sold.toLocaleString()}
                    </div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Top-ups</div>
                    <div className="text-2xl font-bold text-white" data-testid="summary-topup-count">{txSummary.topup_count}</div>
                    <div className="text-xs text-gray-500 mt-1">one-time</div>
                  </div>
                  <div className="rounded-xl p-4 bg-[#222D4A] border border-[#2A3550]">
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Subscriptions</div>
                    <div className="text-2xl font-bold text-white" data-testid="summary-sub-count">{txSummary.subscription_count}</div>
                    <div className="text-xs text-gray-500 mt-1">recurring</div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end" data-testid="transactions-filters">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Email contains</label>
                  <input
                    type="text"
                    value={txEmailSearch}
                    onChange={(e) => setTxEmailSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (setTxPage(0), fetchTransactions())}
                    placeholder="e.g. john@gmail.com"
                    className="w-full px-3 py-2 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="transactions-email-search"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Type</label>
                  <select
                    value={txFilterType}
                    onChange={(e) => { setTxPage(0); setTxFilterType(e.target.value); }}
                    className="px-3 py-2 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm min-w-[140px]"
                    data-testid="transactions-type-filter"
                  >
                    <option value="all">All types</option>
                    <option value="topup">Top-ups</option>
                    <option value="subscription">Subscriptions</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 uppercase tracking-wide mb-1.5">Status</label>
                  <select
                    value={txFilterStatus}
                    onChange={(e) => { setTxPage(0); setTxFilterStatus(e.target.value); }}
                    className="px-3 py-2 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm min-w-[140px]"
                    data-testid="transactions-status-filter"
                  >
                    <option value="all">All statuses</option>
                    <option value="completed">Completed</option>
                    <option value="initiated">Initiated</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              {txLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading transactions…
                </div>
              ) : txs.length === 0 ? (
                <div className="rounded-xl p-8 bg-[#222D4A] border border-[#2A3550] text-center text-gray-400 text-sm" data-testid="transactions-empty">
                  No transactions match these filters.
                </div>
              ) : (
                <div className="rounded-xl bg-[#222D4A] border border-[#2A3550] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="transactions-table">
                      <thead className="bg-[#1A2035] text-gray-400 uppercase text-xs tracking-wide">
                        <tr>
                          <th className="px-4 py-3 text-left">Date</th>
                          <th className="px-4 py-3 text-left">Email</th>
                          <th className="px-4 py-3 text-left">Type</th>
                          <th className="px-4 py-3 text-left">Plan / Pack</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                          <th className="px-4 py-3 text-right">Credits</th>
                          <th className="px-4 py-3 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#2A3550]">
                        {txs.map((tx) => {
                          const d = tx.created_at ? new Date(tx.created_at) : null;
                          const dateStr = d ? d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
                          const planOrPack = tx.plan_id || tx.pack_id || '—';
                          const statusColor = tx.payment_status === 'completed' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                            : tx.payment_status === 'failed' ? 'bg-red-500/15 text-red-400 border-red-500/30'
                            : 'bg-gray-500/15 text-gray-400 border-gray-500/30';
                          return (
                            <tr key={tx.id} className="hover:bg-[#1A2035]/50 transition-colors" data-testid={`tx-row-${tx.id}`}>
                              <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{dateStr}</td>
                              <td className="px-4 py-3 text-white font-medium truncate max-w-[220px]" title={tx.email}>{tx.email}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tx.type === 'topup' ? 'bg-[#FFB238]/15 text-[#FFB238] border-[#FFB238]/30' : 'bg-blue-500/15 text-blue-400 border-blue-500/30'}`}>
                                  {tx.type === 'topup' ? 'Top-up' : tx.type === 'subscription' ? 'Subscription' : tx.type}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-300 capitalize">{planOrPack}</td>
                              <td className="px-4 py-3 text-right text-white font-medium">${tx.amount_paid.toFixed(2)}</td>
                              <td className="px-4 py-3 text-right text-white font-medium">{tx.credits_added.toLocaleString()}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
                                  {tx.payment_status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#1A2035]/60 text-xs text-gray-400 border-t border-[#2A3550]">
                    <div data-testid="transactions-pagination-info">
                      Showing {txPage * TX_PAGE_SIZE + 1}–{Math.min((txPage + 1) * TX_PAGE_SIZE, txTotalCount)} of {txTotalCount}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setTxPage((p) => Math.max(0, p - 1))}
                        disabled={txPage === 0}
                        className="px-3 py-1.5 rounded-md bg-[#222D4A] text-white border border-[#2A3550] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2A3550]"
                        data-testid="transactions-prev-page"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setTxPage((p) => p + 1)}
                        disabled={(txPage + 1) * TX_PAGE_SIZE >= txTotalCount}
                        className="px-3 py-1.5 rounded-md bg-[#222D4A] text-white border border-[#2A3550] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#2A3550]"
                        data-testid="transactions-next-page"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== REPLICATE TAB ==================== */}
          {activeTab === 'replicate' && (
            <div className="space-y-6" data-testid="replicate-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Replicate (Video API)</h2>
                <p className="text-sm text-gray-400">
                  Replicate powers the Kling 3.0 (Steady Vibe) and Seedance 2.0 (Superior Motion) video generators.
                  Update your API token and webhook signing key here.
                </p>
              </div>

              {repLoading ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </div>
              ) : (
                <>
                  {/* API Token */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      API Token{' '}
                      {repTokenSet ? (
                        <span className="ml-2 text-xs text-emerald-400">● Configured</span>
                      ) : (
                        <span className="ml-2 text-xs text-red-400">● Not set</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={repApiToken}
                      onChange={(e) => { setRepApiToken(e.target.value); setRepApiTokenChanged(true); }}
                      placeholder="r8_…"
                      className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                      data-testid="replicate-api-token-input"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Get one at <a className="text-[#FFB238] hover:underline" href="https://replicate.com/account/api-tokens" target="_blank" rel="noreferrer">replicate.com/account/api-tokens</a>.
                    </p>
                  </div>

                  {/* Webhook Signing Key */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Webhook Signing Key{' '}
                      {repSigningKeySet ? (
                        <span className="ml-2 text-xs text-emerald-400">● Configured</span>
                      ) : (
                        <span className="ml-2 text-xs text-red-400">● Not set</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={repSigningKey}
                      onChange={(e) => { setRepSigningKey(e.target.value); setRepSigningKeyChanged(true); }}
                      placeholder="whsec_…"
                      className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                      data-testid="replicate-signing-key-input"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Used to verify Replicate webhooks. Fetch yours from <code className="text-[#FFB238]">GET https://api.replicate.com/v1/webhooks/default/secret</code> or paste the one Replicate emailed you.
                    </p>
                  </div>

                  {/* Webhook Base URL */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Webhook Base URL</label>
                    <input
                      type="text"
                      value={repWebhookBaseUrl}
                      onChange={(e) => setRepWebhookBaseUrl(e.target.value)}
                      placeholder="https://visualatmos.com"
                      className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                      data-testid="replicate-webhook-base-url-input"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Replicate will POST completion events to{' '}
                      <code className="text-white">{repWebhookBaseUrl ? `${repWebhookBaseUrl.replace(/\/$/, '')}/api/replicate/webhook` : '<base>/api/replicate/webhook'}</code>.
                    </p>
                  </div>

                  {/* Resolved webhook endpoint (read-only) */}
                  {repWebhookUrl && (
                    <div className="p-4 rounded-lg bg-[#222D4A] border border-[#2A3550]">
                      <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide mb-1">
                        <Zap className="w-3.5 h-3.5 text-[#FFB238]" /> Active Webhook Endpoint
                      </div>
                      <code className="text-sm text-white break-all" data-testid="replicate-active-webhook-url">{repWebhookUrl}</code>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={fetchReplicateConfig}
                      disabled={repLoading}
                      className="px-4 py-2 rounded-lg bg-[#222D4A] text-white hover:bg-[#2A3550] border border-[#2A3550] text-sm font-medium flex items-center gap-2"
                      data-testid="replicate-refresh-button"
                    >
                      <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button
                      onClick={saveReplicateConfig}
                      disabled={repSaving}
                      className="px-5 py-2 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2 text-sm disabled:opacity-60"
                      data-testid="replicate-save-button"
                    >
                      {repSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Replicate config
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ==================== PROMPT TAB ==================== */}
          {activeTab === 'prompt' && (
            <div className="space-y-6" data-testid="prompt-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Video Generation Prompt</h2>
                <p className="text-sm text-gray-400">
                  This template is sent to the video engine for every generation.
                  Use <code className="text-[#FFB238]">{'{user_input}'}</code> where the user's element list should be inserted.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-[#222D4A] border border-[#2A3550]">
                <p className="text-sm text-[#FFB238] font-medium mb-1">How it works:</p>
                <p className="text-sm text-gray-300">
                  The <code className="text-[#FFB238]">{'{user_input}'}</code> placeholder gets replaced with whatever the user types 
                  (e.g. "fireplace flames, candle flicker"). If user leaves it blank, the default animate text below is used instead.
                </p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Prompt Template</label>
                <p className="text-xs text-gray-500 mb-3">
                  Include <code className="text-[#FFB238]">{'{user_input}'}</code> where user's animation targets should go.
                </p>
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm leading-relaxed"
                  rows={6}
                  spellCheck={false}
                  data-testid="prompt-text-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Default Animate Text</label>
                <p className="text-xs text-gray-500 mb-3">
                  Replaces <code className="text-[#FFB238]">{'{user_input}'}</code> when the user doesn't provide their own prompt.
                </p>
                <textarea
                  value={defaultAnimate}
                  onChange={(e) => setDefaultAnimate(e.target.value)}
                  placeholder="subtle ambient motion such as flickering flames, drifting dust particles, or gently swaying foliage"
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                  rows={3}
                  data-testid="default-animate-input"
                />
              </div>

              {/* ============ VEO / LIVING MOTION PROMPT ============ */}
              <div className="mt-8 pt-6 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-2">Living Motion (Veo 3.1 Fast) Prompts</h3>
                <p className="text-sm text-gray-400 mb-4">Separate prompt template for the Living Motion model. Used when user selects Living Motion in the Generate page.</p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Veo Prompt Template</label>
                <p className="text-xs text-gray-500 mb-3">
                  Include <code className="text-[#FFB238]">{'{user_input}'}</code> where the user's prompt should be inserted.
                </p>
                <textarea
                  value={veoPromptText}
                  onChange={(e) => setVeoPromptText(e.target.value)}
                  placeholder=""
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm leading-relaxed"
                  rows={6}
                  spellCheck={false}
                  data-testid="veo-prompt-text-input"
                />
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Veo Default Animate Text</label>
                <p className="text-xs text-gray-500 mb-3">
                  Replaces <code className="text-[#FFB238]">{'{user_input}'}</code> for Living Motion when user leaves their prompt blank.
                </p>
                <textarea
                  value={veoDefaultAnimate}
                  onChange={(e) => setVeoDefaultAnimate(e.target.value)}
                  placeholder="gentle wind moving through foliage, soft rain, or drifting clouds"
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                  rows={3}
                  data-testid="veo-default-animate-input"
                />
              </div>

              {/* ============ BYPASS KEYWORDS ============ */}
              <div className="mt-8 pt-6 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-2">Template Bypass Keywords</h3>
                <p className="text-sm text-gray-400 mb-4">
                  If a user's prompt contains <strong className="text-white">any</strong> of these phrases (whole-word match,
                  case-insensitive), the template above is <strong className="text-white">skipped entirely</strong> and we send only the user's text
                  to the model. Use this to stop the default "camera locked" instruction from overriding
                  explicit camera-motion requests like <em className="text-[#FFB238]">"cinematic camera movement"</em>.
                </p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Bypass Keywords (one per line)</label>
                <p className="text-xs text-gray-500 mb-3">
                  Applies to both Steady Vibe and Superior Motion. Whole-phrase match — <code className="text-[#FFB238]">pan</code> will NOT trip on "panoramic".
                </p>
                <textarea
                  value={bypassKeywords}
                  onChange={(e) => setBypassKeywords(e.target.value)}
                  placeholder="camera movement&#10;camera motion&#10;cinematic camera&#10;zoom in&#10;zoom out&#10;drone shot"
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm font-mono"
                  rows={8}
                  spellCheck={false}
                  data-testid="bypass-keywords-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Currently {bypassKeywords.split('\n').map((s) => s.trim()).filter(Boolean).length} keyword{bypassKeywords.split('\n').map((s) => s.trim()).filter(Boolean).length === 1 ? '' : 's'} configured.
                </p>
              </div>
            </div>
          )}

          {/* ==================== PROMPT MANAGEMENT TAB (Image Transformation) ==================== */}
          {activeTab === 'transform_prompts' && (
            <div className="space-y-6" data-testid="transform-prompts-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Image Transformation Prompts</h2>
                <p className="text-sm text-gray-400">
                  Text prefixes injected into the master template when a user activates a
                  Season / Daytime / Weather / Resolution option in the Transform Image tool. Powered by
                  Vertex AI <code className="text-[#FFB238]">gemini-3-pro-image-preview</code>.
                </p>
              </div>

              {/* ── Season prefixes ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-3">Season Prefixes</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {['summer', 'winter', 'autumn', 'spring'].map((k) => (
                    <div key={k}>
                      <label className="block text-white font-medium mb-2 capitalize">{k}</label>
                      <textarea
                        value={transformSeasonPrefixes[k]}
                        onChange={(e) => setTransformSeasonPrefixes({ ...transformSeasonPrefixes, [k]: e.target.value })}
                        placeholder={`Text injected for {season} when "${k}" is active`}
                        className="w-full px-3 py-2 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        rows={2}
                        data-testid={`transform-season-prefix-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Daytime prefixes ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-3">Daytime Prefixes</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {['morning', 'afternoon', 'evening', 'late_night'].map((k) => (
                    <div key={k}>
                      <label className="block text-white font-medium mb-2 capitalize">{k.replace('_', ' ')}</label>
                      <textarea
                        value={transformDaytimePrefixes[k]}
                        onChange={(e) => setTransformDaytimePrefixes({ ...transformDaytimePrefixes, [k]: e.target.value })}
                        placeholder={`Text injected for {daytime} when "${k}" is active`}
                        className="w-full px-3 py-2 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        rows={2}
                        data-testid={`transform-daytime-prefix-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Weather prefixes ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-3">Weather Prefixes</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {['rainfall', 'snowfall', 'overcast', 'clear_sky'].map((k) => (
                    <div key={k}>
                      <label className="block text-white font-medium mb-2 capitalize">{k.replace('_', ' ')}</label>
                      <textarea
                        value={transformWeatherPrefixes[k]}
                        onChange={(e) => setTransformWeatherPrefixes({ ...transformWeatherPrefixes, [k]: e.target.value })}
                        placeholder={`Text injected for {weather} when "${k}" is active`}
                        className="w-full px-3 py-2 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        rows={2}
                        data-testid={`transform-weather-prefix-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Resolution prefixes ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-3">Resolution Prefixes</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {['1080p', '4k'].map((k) => (
                    <div key={k}>
                      <label className="block text-white font-medium mb-2 uppercase">{k}</label>
                      <textarea
                        value={transformResolutionPrefixes[k]}
                        onChange={(e) => setTransformResolutionPrefixes({ ...transformResolutionPrefixes, [k]: e.target.value })}
                        placeholder={`Text injected for {resolution} when "${k}" is selected`}
                        className="w-full px-3 py-2 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                        rows={2}
                        data-testid={`transform-resolution-prefix-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* ── Custom description placeholder (user-facing hint) ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-2">
                  Custom Description Placeholder
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  The greyed hint text inside the &ldquo;Custom description&rdquo; textarea on the
                  Transform Image tab. Purely UI copy — not sent to the model. Keep it friendly and
                  give examples so hobbyist users know what to type.
                </p>
                <textarea
                  value={transformDescriptionPlaceholder}
                  onChange={(e) => setTransformDescriptionPlaceholder(e.target.value)}
                  placeholder="Falling leaves, Halloween theme, Christmas vibe, cozy candles on the table, or anything else you'd like to include."
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                  rows={3}
                  data-testid="transform-description-placeholder-input"
                />
              </div>

              {/* ── Master template ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-2">Master Prompt Template</h3>
                <p className="text-sm text-gray-400 mb-3">
                  Final prompt sent to Gemini 3 Pro Image. Available variables (each substitutes to the
                  active prefixes joined by ", "):{' '}
                  <code className="text-[#FFB238]">{'{season}'}</code>{' '}
                  <code className="text-[#FFB238]">{'{daytime}'}</code>{' '}
                  <code className="text-[#FFB238]">{'{weather}'}</code>{' '}
                  <code className="text-[#FFB238]">{'{resolution}'}</code>{' '}
                  <code className="text-[#FFB238]">{'{user_input}'}</code> — user's custom description.
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  <strong className="text-white">Required:</strong>{' '}
                  <code className="text-[#FFB238]">{'{user_input}'}</code> must appear at least once so
                  the user's description reaches the model.
                </p>
                <textarea
                  value={transformMasterTemplate}
                  onChange={(e) => setTransformMasterTemplate(e.target.value)}
                  placeholder="Transform this image while preserving its composition. Apply: {season}, {daytime}, {weather}. User: {user_input}. Target: {resolution}."
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm leading-relaxed"
                  rows={7}
                  spellCheck={false}
                  data-testid="transform-master-template-input"
                />
                {!transformMasterTemplate.includes('{user_input}') && (
                  <p className="text-xs text-red-400 mt-2">
                    Warning: your template does not contain <code>{'{user_input}'}</code>. The user's
                    custom description will not reach the model.
                  </p>
                )}
              </div>

              {/* ── Example carousel thumbnails ── */}
              <div className="pt-4 border-t border-[#2A3550]">
                <h3 className="text-lg font-bold text-white mb-2">Example Carousel Thumbnails</h3>
                <p className="text-sm text-gray-400 mb-2">
                  Upload example images to{' '}
                  <code className="text-[#FFB238]">gs://&lt;bucket&gt;/examples/transform/</code>{' '}
                  in Google Cloud Console, then run the button below to auto-downscale each image
                  to at most 800px wide (JPEG, ~82% quality) and cache it under
                  <code className="text-[#FFB238] ml-1">examples/transform/thumbnails/</code>.
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  The Transform Image tab shows the first 4 images. Only the tiny thumbnails are
                  loaded on the public page, so the carousel stays fast even with large source
                  originals. Rerun with "Force regenerate" after replacing a source image.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleGenerateTransformThumbnails(false)}
                    disabled={xformThumbLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors disabled:opacity-60"
                    data-testid="admin-generate-transform-thumbs"
                  >
                    {xformThumbLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Regenerating…</>
                    ) : (
                      <><RefreshCw className="w-4 h-4" /> Regenerate Thumbnails</>
                    )}
                  </button>
                  <button
                    onClick={() => handleGenerateTransformThumbnails(true)}
                    disabled={xformThumbLoading}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#222D4A] text-white font-medium border border-[#2A3550] hover:border-[#FFB238]/50 transition-colors disabled:opacity-60"
                    data-testid="admin-generate-transform-thumbs-force"
                    title="Regenerate thumbnails even for images that already have one"
                  >
                    {xformThumbLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Working…</>
                    ) : (
                      <>Force regenerate all</>
                    )}
                  </button>
                </div>
                {xformThumbResult && (
                  <p
                    className={`mt-3 text-sm ${xformThumbResult.ok ? 'text-green-400' : 'text-red-400'}`}
                    data-testid="admin-transform-thumb-result"
                  >
                    {xformThumbResult.message}
                    {xformThumbResult.ok && Array.isArray(xformThumbResult.errors) && xformThumbResult.errors.length > 0 && (
                      <span className="block text-xs text-red-400 mt-1">
                        First error: {xformThumbResult.errors[0].file} — {xformThumbResult.errors[0].error}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ==================== RAIN CANVAS TAB ==================== */}
          {activeTab === 'rain_canvas' && (
            <div className="space-y-6" data-testid="rain-canvas-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Rain Canvas Masking Prompts</h2>
                <p className="text-sm text-gray-400">
                  Text prompts sent to SAM 3 (<code className="text-[#FFB238]">fal-ai/sam-3/image</code>)
                  to decide where the weather effect is applied. Enter a comma-separated list — each
                  prompt is tried in order and the first one that returns a mask is used. Leave a field
                  blank to use the built-in defaults.
                </p>
              </div>

              <div className="pt-4 border-t border-[#2A3550]">
                <label className="block text-white font-medium mb-2">Interior scene prompts</label>
                <p className="text-xs text-gray-500 mb-3">
                  Used when the user picks <strong className="text-white">Interior</strong> — targets the
                  view beyond a window so rain/snow falls outside the glass.
                </p>
                <textarea
                  value={rainInteriorPrompts}
                  onChange={(e) => setRainInteriorPrompts(e.target.value)}
                  placeholder={RAIN_DEFAULT_INTERIOR}
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                  rows={2}
                  data-testid="rain-interior-prompts-input"
                />
                <p className="text-xs text-gray-500 mt-1">Default: {RAIN_DEFAULT_INTERIOR}</p>
              </div>

              <div className="pt-4 border-t border-[#2A3550]">
                <label className="block text-white font-medium mb-2">Outdoors scene prompts</label>
                <p className="text-xs text-gray-500 mb-3">
                  Used when the user picks <strong className="text-white">Outdoors</strong> — targets open
                  sky / background so weather covers the exterior scene.
                </p>
                <textarea
                  value={rainOutdoorsPrompts}
                  onChange={(e) => setRainOutdoorsPrompts(e.target.value)}
                  placeholder={RAIN_DEFAULT_OUTDOORS}
                  className="w-full px-4 py-3 rounded-lg bg-[#0F1419] text-gray-200 border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                  rows={2}
                  data-testid="rain-outdoors-prompts-input"
                />
                <p className="text-xs text-gray-500 mt-1">Default: {RAIN_DEFAULT_OUTDOORS}</p>
              </div>

              <p className="text-xs text-gray-500">
                Use the <strong className="text-white">Save Configuration</strong> button at the top to apply changes.
              </p>
            </div>
          )}

          {/* ==================== GENERAL TAB ==================== */}
          {activeTab === 'general' && (
            <div className="space-y-6" data-testid="general-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Stripe Configuration</h2>
                <p className="text-sm text-gray-400">Your Stripe API keys for payments and subscriptions</p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Stripe Secret Key</label>
                <p className="text-sm text-gray-400 mb-3">
                  Status: {stripeKey ? (
                    stripeKey.includes('••••') ? <span className="text-green-400">Configured (masked)</span>
                    : stripeKey.includes('test') ? <span className="text-yellow-400">Test Mode</span>
                    : <span className="text-green-400">Live Mode</span>
                  ) : <span className="text-red-400">Not configured</span>}
                </p>
                <input
                  type="text"
                  value={stripeKey}
                  onChange={(e) => { setStripeKey(e.target.value); setStripeKeyChanged(true); }}
                  onFocus={() => { if (stripeKey.includes('••••')) { setStripeKey(''); setStripeKeyChanged(true); } }}
                  placeholder="sk_live_... or sk_test_..."
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                  data-testid="stripe-key-input"
                />
                <p className="text-xs text-gray-500 mt-2">Paste your full Stripe secret key. It will be masked after saving.</p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Webhook Signing Secret</label>
                <p className="text-sm text-gray-400 mb-3">
                  Status: {stripeWebhookSecret ? (
                    stripeWebhookSecret.includes('••••') ? <span className="text-green-400">Configured (masked)</span>
                    : <span className="text-green-400">Set</span>
                  ) : <span className="text-red-400">Not configured</span>}
                </p>
                <input
                  type="text"
                  value={stripeWebhookSecret}
                  onChange={(e) => { setStripeWebhookSecret(e.target.value); setStripeWebhookChanged(true); }}
                  onFocus={() => { if (stripeWebhookSecret.includes('••••')) { setStripeWebhookSecret(''); setStripeWebhookChanged(true); } }}
                  placeholder="whsec_..."
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono text-sm"
                  data-testid="stripe-webhook-secret-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Found in Stripe Dashboard → Developers → Webhooks → your endpoint → Signing secret
                </p>
              </div>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-[#FFB238] font-medium mb-1">Webhook Endpoint</p>
                <p className="text-sm text-gray-300 font-mono break-all">
                  {window.location.origin}/api/webhook/stripe
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  Events: checkout.session.completed, invoice.paid, invoice.payment_failed, customer.subscription.deleted, customer.subscription.updated
                </p>
              </div>
            </div>
          )}

          {/* ==================== ANALYTICS TAB ==================== */}
          {activeTab === 'analytics' && (
            <div className="space-y-6" data-testid="analytics-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Google Analytics</h2>
                <p className="text-sm text-gray-400">Track traffic and user behavior on your website</p>
              </div>

              <div>
                <label className="block text-white font-medium mb-2">Measurement ID</label>
                <p className="text-sm text-gray-400 mb-3">
                  Found in Google Analytics → Admin → Data Streams → your stream → Measurement ID
                </p>
                <input
                  type="text"
                  value={gaMeasurementId}
                  onChange={(e) => setGaMeasurementId(e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  className="w-full px-4 py-3 rounded-lg bg-[#222D4A] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none font-mono"
                  data-testid="ga-measurement-id-input"
                />
              </div>

              <button
                onClick={handleSaveAnalytics}
                disabled={savingAnalytics}
                className="px-6 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2"
                data-testid="save-analytics-button"
              >
                {savingAnalytics ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Analytics Config
              </button>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-[#FFB238] font-medium mb-1">How it works</p>
                <p className="text-sm text-gray-300">
                  Once saved, the Google Analytics tracking script is automatically injected into every page of your site. Changes take effect immediately on the next page load.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'gifts' && (
            <div className="space-y-6" data-testid="gifts-tab-content">
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Gift Credits</h2>
                <p className="text-sm text-gray-400">Pre-load credits for users before they sign up. Credits are automatically added to their account on registration.</p>
              </div>

              {/* Create Gift Form */}
              <form onSubmit={handleCreateGift} className="p-6 rounded-xl bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="create-gift-form">
                <h3 className="text-white font-semibold">Create New Gift</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Email</label>
                    <input
                      type="email"
                      value={giftEmail}
                      onChange={(e) => setGiftEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none"
                      data-testid="gift-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Credits</label>
                    <input
                      type="number"
                      value={giftCredits}
                      onChange={(e) => setGiftCredits(parseInt(e.target.value) || 0)}
                      min="1"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none"
                      data-testid="gift-credits-input"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Expires in (days)</label>
                    <input
                      type="number"
                      value={giftExpiry}
                      onChange={(e) => setGiftExpiry(parseInt(e.target.value) || 1)}
                      min="1"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none"
                      data-testid="gift-expiry-input"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={giftLoading}
                  className="px-6 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2 disabled:opacity-50"
                  data-testid="create-gift-button"
                >
                  {giftLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                  Create Gift
                </button>
              </form>

              {/* Gifts Table */}
              <div className="rounded-xl border border-[#2A3550] overflow-x-auto" data-testid="gifts-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#222D4A]">
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Email</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Credits</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Created</th>
                      <th className="text-left px-4 py-3 text-gray-400 font-medium">Expires</th>
                      <th className="text-right px-4 py-3 text-gray-400 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gifts.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">No gifts created yet</td>
                      </tr>
                    )}
                    {gifts.map((gift) => (
                      <tr key={gift.id} className="border-t border-[#2A3550] hover:bg-[#222D4A]/50">
                        <td className="px-4 py-3 text-white font-mono text-xs">{gift.email}</td>
                        <td className="px-4 py-3 text-[#FFB238] font-bold">{gift.credits.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${
                            gift.status === 'claimed' ? 'bg-green-500/15 text-green-400' :
                            gift.status === 'expired' ? 'bg-red-500/15 text-red-400' :
                            'bg-amber-500/15 text-amber-400'
                          }`}>
                            {gift.status.charAt(0).toUpperCase() + gift.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(gift.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(gift.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td className="px-4 py-3 text-right">
                          {gift.status === 'pending' && (
                            <button
                              onClick={() => handleDeleteGift(gift.id)}
                              disabled={deletingGift === gift.id}
                              className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                              data-testid={`delete-gift-${gift.id}`}
                            >
                              {deletingGift === gift.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-sm text-blue-400 font-medium mb-1">How gifts work</p>
                <p className="text-sm text-gray-300">
                  Pending gifts are automatically added as <strong>top-up credits</strong> when the recipient signs up. If they're already registered, credits are added immediately. Expired gifts are cleaned up automatically. You can gift the same email multiple times.
                </p>
              </div>

              {/* Check User Balance */}
              <div className="p-6 rounded-xl bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="check-user-section">
                <h3 className="text-white font-semibold">Check User Balance</h3>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={checkEmail}
                    onChange={(e) => setCheckEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="check-user-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && checkEmail && handleCheckUser(checkEmail)}
                  />
                  <button
                    onClick={() => handleCheckUser(checkEmail)}
                    disabled={!checkEmail || checkLoading}
                    className="px-5 py-2.5 rounded-lg bg-[#2A3550] text-white font-medium hover:bg-[#354060] transition-colors disabled:opacity-50 text-sm"
                    data-testid="check-user-button"
                  >
                    {checkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Check'}
                  </button>
                </div>
                {checkedUser && (
                  <div className="p-4 rounded-lg bg-[#1A2035] border border-[#2A3550] space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Email</span>
                      <span className="text-white text-sm font-mono">{checkedUser.user.email}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Total Balance</span>
                      <span className="text-[#FFB238] text-sm font-bold">{(checkedUser.user.credits_balance || 0).toLocaleString()} credits</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Subscription Credits</span>
                      <span className="text-white text-sm">{(checkedUser.user.subscription_credits || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Top-up Credits</span>
                      <span className="text-white text-sm">{(checkedUser.user.topup_credits || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Tier</span>
                      <span className="text-white text-sm capitalize">{checkedUser.user.subscription_tier || 'free'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Gifts</span>
                      <span className="text-white text-sm">{checkedUser.gifts.length} total ({checkedUser.gifts.filter(g => g.status === 'claimed').length} claimed)</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Restore Deleted Email */}
              <div className="p-6 rounded-xl bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="restore-email-section">
                <div>
                  <h3 className="text-white font-semibold">Restore Deleted Email</h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Removes an email from the soft-deleted block-list so the user can sign in with Google again.
                    Use only for legitimate "I accidentally deleted my account" support cases.
                  </p>
                </div>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={restoreEmail}
                    onChange={(e) => setRestoreEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="restore-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && restoreEmail && handleRestoreEmail(restoreEmail)}
                  />
                  <button
                    onClick={() => handleRestoreEmail(restoreEmail)}
                    disabled={!restoreEmail || restoreLoading}
                    className="px-5 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors disabled:opacity-50 text-sm"
                    data-testid="restore-email-button"
                  >
                    {restoreLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restore'}
                  </button>
                </div>
                {restoreResult && (
                  <div className={`p-3 rounded-lg text-sm ${restoreResult.ok ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`} data-testid="restore-email-result">
                    {restoreResult.message}
                    {restoreResult.previously_deleted_at && (
                      <div className="text-xs text-gray-400 mt-1">
                        Was deleted on: {new Date(restoreResult.previously_deleted_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Refund / Deduct Credits */}
              <div className="p-6 rounded-xl bg-[#222D4A] border border-red-500/30 space-y-4" data-testid="adjust-credits-section">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-400"></span>
                    Refund / Deduct Credits
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Remove credits from a user after issuing a Stripe refund. Does NOT touch Stripe —
                    refund the payment in the Stripe dashboard separately. All deductions are logged to
                    the <code className="text-gray-300">admin_adjustments</code> collection.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <input
                    type="email"
                    value={adjustEmail}
                    onChange={(e) => setAdjustEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="sm:col-span-3 px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="adjust-credits-email-input"
                  />
                  <input
                    type="number"
                    min="1"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    placeholder="Amount (e.g. 250)"
                    className="sm:col-span-2 px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="adjust-credits-amount-input"
                  />
                  <select
                    value={adjustBucket}
                    onChange={(e) => setAdjustBucket(e.target.value)}
                    className="sm:col-span-1 px-3 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="adjust-credits-bucket-select"
                  >
                    <option value="auto">Auto</option>
                    <option value="topup">Top-up</option>
                    <option value="subscription">Subscription</option>
                  </select>
                </div>

                <textarea
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder="Reason (optional but recommended — e.g. 'Stripe refund ch_ABC123, user requested')"
                  rows={2}
                  className="w-full px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm resize-none"
                  data-testid="adjust-credits-reason-input"
                />

                <label className="flex items-start gap-3 p-3 rounded-lg bg-[#1A2035] border border-[#2A3550] cursor-pointer hover:border-red-500/40 transition-colors" data-testid="adjust-credits-cancel-sub-label">
                  <input
                    type="checkbox"
                    checked={adjustCancelSub}
                    onChange={(e) => setAdjustCancelSub(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-red-500"
                    data-testid="adjust-credits-cancel-sub-checkbox"
                  />
                  <span className="text-sm text-gray-300">
                    <span className="font-medium text-white">Also cancel active subscription</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Immediately cancels the user's Stripe subscription and downgrades them to <code className="text-gray-300">free</code>.
                      Use this when the refund is for the monthly plan itself, not a top-up.
                    </span>
                  </span>
                </label>

                <div className="flex justify-end">
                  <button
                    onClick={handleAdjustCredits}
                    disabled={!adjustEmail || !adjustAmount || adjustLoading}
                    className="px-5 py-2.5 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors disabled:opacity-50 text-sm flex items-center gap-2"
                    data-testid="adjust-credits-submit-button"
                  >
                    {adjustLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Deduct credits
                  </button>
                </div>

                {adjustResult && (
                  <div
                    className={`p-3 rounded-lg text-sm ${adjustResult.ok ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`}
                    data-testid="adjust-credits-result"
                  >
                    <div className="font-medium">{adjustResult.message}</div>
                    {adjustResult.ok && adjustResult.before && (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-300 mt-2 font-mono">
                        <div>Top-up: {adjustResult.before.topup_credits} → {adjustResult.after.topup_credits}</div>
                        <div>Subscription: {adjustResult.before.subscription_credits} → {adjustResult.after.subscription_credits}</div>
                        <div className="col-span-2 text-white">Total balance: {adjustResult.before.credits_balance} → {adjustResult.after.credits_balance}</div>
                        {adjustResult.before.subscription_tier !== adjustResult.after.subscription_tier && (
                          <div className="col-span-2 text-red-300">
                            Tier: {adjustResult.before.subscription_tier} → {adjustResult.after.subscription_tier}
                          </div>
                        )}
                        {adjustResult.adjustment_id && (
                          <div className="col-span-2 text-gray-500">Audit id: {adjustResult.adjustment_id}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Force-Claim Pending Gifts */}
              <div className="p-6 rounded-xl bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="force-claim-section">
                <div>
                  <h3 className="text-white font-semibold">Force-Claim Pending Gifts</h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Directly credits ALL pending gifts for an email to that user's account.
                    Use when a user got stuck on the email-claim flow. The user must already have an account.
                  </p>
                </div>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={forceClaimEmail}
                    onChange={(e) => setForceClaimEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="flex-1 px-4 py-2.5 rounded-lg bg-[#1A2035] text-white border border-[#2A3550] focus:border-[#FFB238] focus:outline-none text-sm"
                    data-testid="force-claim-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && forceClaimEmail && handleForceClaim(forceClaimEmail)}
                  />
                  <button
                    onClick={() => handleForceClaim(forceClaimEmail)}
                    disabled={!forceClaimEmail || forceClaimLoading}
                    className="px-5 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors disabled:opacity-50 text-sm"
                    data-testid="force-claim-button"
                  >
                    {forceClaimLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Force-Claim'}
                  </button>
                </div>
                {forceClaimResult && (
                  <div className={`p-3 rounded-lg text-sm ${forceClaimResult.ok ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`} data-testid="force-claim-result">
                    {forceClaimResult.message}
                    {forceClaimResult.ok && forceClaimResult.new_balance != null && (
                      <div className="text-xs text-gray-400 mt-1">
                        New balance: <span className="text-white font-medium">{forceClaimResult.new_balance.toLocaleString()} credits</span>
                        {forceClaimResult.claimed > 0 && ` · ${forceClaimResult.claimed} gift(s) claimed`}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Generate Example Thumbnails */}
              <div className="p-6 rounded-xl bg-[#222D4A] border border-[#2A3550] space-y-4" data-testid="thumb-gen-section">
                <div>
                  <h3 className="text-white font-semibold">Generate Example Thumbnails</h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Scans the <code className="text-amber-300">examples/</code> bucket prefix and creates a JPEG thumbnail at <code className="text-amber-300">examples/thumbnails/{'{stem}'}.jpg</code> for every video that doesn't already have one. Run this once after adding new example videos.
                  </p>
                </div>
                <button
                  onClick={handleGenerateThumbnails}
                  disabled={thumbLoading}
                  className="px-5 py-2.5 rounded-lg bg-[#FFB238] text-[#0F1419] font-medium hover:bg-[#FFDA82] transition-colors disabled:opacity-50 text-sm inline-flex items-center gap-2"
                  data-testid="thumb-gen-button"
                >
                  {thumbLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : 'Generate Missing Thumbnails'}
                </button>
                {thumbResult && (
                  <div className={`p-3 rounded-lg text-sm ${thumbResult.ok ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300'}`} data-testid="thumb-gen-result">
                    {thumbResult.message}
                    {thumbResult.ok && Array.isArray(thumbResult.errors) && thumbResult.errors.length > 0 && (
                      <ul className="mt-2 text-xs text-red-300 list-disc list-inside">
                        {thumbResult.errors.slice(0, 5).map((e) => (
                          <li key={e.file}><span className="text-gray-400">{e.file}:</span> {e.error}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Save bar at bottom */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 rounded-lg bg-[#FFB238] text-[#0F1419] font-bold hover:bg-[#FFDA82] transition-colors flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Save All Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminConfig;
